export interface LearnSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface LearnTopicLocale {
  title: string;
  summary: string;
  sections: LearnSection[];
}

export type LearnCategory =
  | "foundations"
  | "history"
  | "development"
  | "geopolitics"
  | "society"
  | "reading-data";

export interface LearnTopic {
  slug: string;
  /**
   * The slug an English reader sees in the URL. `slug` stays the canonical
   * id — `related` arrays and every internal lookup use it — so translating
   * the URLs never moves anything that points at an item.
   */
  slugEn: string;
  emoji: string;
  category: LearnCategory;
  // Slugs of thematically related topics, shown at the bottom of the
  // article so the section reads as a connected web instead of a flat list.
  related?: string[];
  es: LearnTopicLocale;
  en: LearnTopicLocale;
}

export const LEARN_CATEGORY_LABELS: Record<
  LearnCategory,
  { es: string; en: string }
> = {
  foundations: { es: "Conceptos fundamentales", en: "Foundational concepts" },
  history: { es: "Historia y conflictos", en: "History & conflicts" },
  development: { es: "Economía y desarrollo", en: "Economy & development" },
  geopolitics: { es: "Geografía política", en: "Political geography" },
  society: { es: "Sociedad y cultura", en: "Society & culture" },
  "reading-data": { es: "Cómo leer los datos", en: "Reading the data" },
};

export const learnTopics: LearnTopic[] = [
  {
    slug: "primer-tercer-mundo",
    slugEn: "first-second-third-world",
    emoji: "🌍",
    category: "development",
    related: ["guerra-fria", "pbi-idh", "cambio-climatico"],
    es: {
      title: "Primer, Segundo y Tercer Mundo",
      summary:
        "De dónde vienen estos términos y por qué hoy se prefiere hablar de países desarrollados y en desarrollo.",
      sections: [
        {
          heading: "¿De dónde vienen los términos?",
          paragraphs: [
            "Durante la Guerra Fría (1947-1991), el mundo se pensaba dividido en tres bloques. El Primer Mundo eran los países capitalistas alineados con Estados Unidos y Europa Occidental. El Segundo Mundo eran los países comunistas alineados con la Unión Soviética. El Tercer Mundo agrupaba al resto: países no alineados con ninguno de los dos bloques, mayormente en África, Asia y América Latina, muchos de ellos recién independizados de la colonización europea.",
            "El término lo acuñó en 1952 el demógrafo francés Alfred Sauvy, inspirado en el concepto del \"tercer estado\" de la Revolución Francesa.",
          ],
        },
        {
          heading: "Por qué el término quedó obsoleto",
          paragraphs: [
            "Con la caída de la URSS en 1991, la división política en tres bloques dejó de tener sentido. Sin embargo, la expresión \"Tercer Mundo\" siguió usándose de forma coloquial para referirse a países pobres, un uso impreciso que hoy se considera anticuado y despectivo.",
          ],
        },
        {
          heading: "País desarrollado vs. en desarrollo",
          paragraphs: [
            "Los organismos internacionales actuales prefieren hablar de países \"desarrollados\", \"en desarrollo\" o \"menos desarrollados\". La clasificación se basa en indicadores concretos, no en bloques políticos:",
          ],
          list: [
            "Índice de Desarrollo Humano (IDH): combina salud, educación e ingresos.",
            "PBI per cápita: riqueza económica dividida por la población.",
            "Esperanza de vida y acceso a salud y educación.",
          ],
        },
      ],
    },
    en: {
      title: "First, Second and Third World",
      summary:
        "Where these terms came from, and why today's language favors developed vs. developing countries.",
      sections: [
        {
          heading: "Where the terms come from",
          paragraphs: [
            "During the Cold War (1947-1991), the world was seen as split into three blocs. The First World was the capitalist countries aligned with the United States and Western Europe. The Second World was the communist countries aligned with the Soviet Union. The Third World grouped everyone else: countries aligned with neither bloc, mostly in Africa, Asia and Latin America, many of them recently independent from European colonization.",
            "French demographer Alfred Sauvy coined the term in 1952, inspired by the \"third estate\" of the French Revolution.",
          ],
        },
        {
          heading: "Why the term became outdated",
          paragraphs: [
            "When the USSR collapsed in 1991, the three-bloc political split stopped making sense. Even so, \"Third World\" kept being used colloquially to mean \"poor country\" — an imprecise usage now considered outdated and pejorative.",
          ],
        },
        {
          heading: "Developed vs. developing",
          paragraphs: [
            "International organizations now favor \"developed\", \"developing\" or \"least developed\" countries. The classification rests on concrete indicators, not political blocs:",
          ],
          list: [
            "Human Development Index (HDI): combines health, education and income.",
            "GDP per capita: economic output divided by population.",
            "Life expectancy and access to healthcare and education.",
          ],
        },
      ],
    },
  },
  {
    slug: "guerra-fria",
    slugEn: "cold-war",
    emoji: "🧊",
    category: "history",
    related: [
      "guerras-mundiales",
      "capitalismo-y-alternativas",
      "revolucion-rusa",
    ],
    es: {
      title: "La Guerra Fría: capitalismo vs. comunismo",
      summary:
        "Qué fue el enfrentamiento entre EE.UU. y la URSS, y qué diferencia al capitalismo del comunismo.",
      sections: [
        {
          heading: "¿Qué fue la Guerra Fría?",
          paragraphs: [
            "Fue un enfrentamiento indirecto (1947-1991) entre Estados Unidos y la Unión Soviética, las dos superpotencias que quedaron tras la Segunda Guerra Mundial. Nunca combatieron entre sí de forma directa, pero se enfrentaron a través de guerras \"por delegación\" en Corea, Vietnam y Afganistán, además de una carrera armamentística nuclear y una carrera espacial.",
          ],
        },
        {
          heading: "Capitalismo",
          paragraphs: [
            "Sistema económico basado en la propiedad privada de los medios de producción, el libre mercado y la búsqueda de ganancia como principal motor de la economía. Es el sistema predominante en Estados Unidos y Europa Occidental durante la Guerra Fría, y en la mayoría del mundo hoy.",
          ],
        },
        {
          heading: "Comunismo",
          paragraphs: [
            "Sistema político y económico que busca la propiedad colectiva de los medios de producción y una sociedad sin clases sociales. En la práctica, los estados comunistas del siglo XX (la URSS, China, Cuba) fueron gobernados por un partido único con fuerte control estatal de la economía.",
          ],
        },
        {
          heading: "La Unión Soviética (URSS)",
          paragraphs: [
            "Existió entre 1922 y 1991 como una federación de 15 repúblicas socialistas liderada por Rusia. Fue la principal potencia comunista del siglo XX y se disolvió en diciembre de 1991, dando origen a Rusia y a otros 14 países independientes (entre ellos Ucrania, Kazajistán y las repúblicas bálticas).",
          ],
        },
        {
          heading: "Momentos clave",
          list: [
            "Bloqueo de Berlín (1948-1949): la URSS cortó el acceso terrestre a Berlín Occidental; EE.UU. y sus aliados respondieron abasteciendo la ciudad por aire durante casi un año.",
            "Construcción del Muro de Berlín (1961): dividió la ciudad para frenar la fuga de alemanes orientales hacia el oeste, y se convirtió en el símbolo físico más conocido de la Guerra Fría.",
            "Crisis de los misiles de Cuba (1962): la URSS instaló misiles nucleares en Cuba; el enfrentamiento con EE.UU. llevó al mundo más cerca que nunca de una guerra nuclear directa.",
            "Caída del Muro de Berlín (1989): marcó el principio del fin de los regímenes comunistas de Europa del Este.",
            "Disolución de la URSS (diciembre de 1991): puso fin formal a la Guerra Fría.",
          ],
        },
      ],
    },
    en: {
      title: "The Cold War: capitalism vs. communism",
      summary:
        "What the standoff between the US and the USSR was about, and what tells capitalism apart from communism.",
      sections: [
        {
          heading: "What was the Cold War?",
          paragraphs: [
            "It was an indirect standoff (1947-1991) between the United States and the Soviet Union, the two superpowers left standing after World War II. They never fought each other directly, but clashed through proxy wars in Korea, Vietnam and Afghanistan, alongside a nuclear arms race and a space race.",
          ],
        },
        {
          heading: "Capitalism",
          paragraphs: [
            "An economic system based on private ownership of the means of production, free markets, and profit as the main engine of the economy. It was the dominant system in the US and Western Europe during the Cold War, and across most of the world today.",
          ],
        },
        {
          heading: "Communism",
          paragraphs: [
            "A political and economic system that seeks collective ownership of the means of production and a classless society. In practice, 20th-century communist states (the USSR, China, Cuba) were run by a single party with heavy state control of the economy.",
          ],
        },
        {
          heading: "The Soviet Union (USSR)",
          paragraphs: [
            "It existed from 1922 to 1991 as a federation of 15 socialist republics led by Russia. It was the leading communist power of the 20th century and dissolved in December 1991, giving rise to Russia and 14 other independent countries (including Ukraine, Kazakhstan and the Baltic states).",
          ],
        },
        {
          heading: "Key moments",
          list: [
            "Berlin Blockade (1948-1949): the USSR cut off land access to West Berlin; the US and its allies responded by supplying the city by air for almost a year.",
            "Construction of the Berlin Wall (1961): split the city to stop East Germans from fleeing west, becoming the best-known physical symbol of the Cold War.",
            "Cuban Missile Crisis (1962): the USSR installed nuclear missiles in Cuba; the standoff with the US brought the world closer than ever to direct nuclear war.",
            "Fall of the Berlin Wall (1989): marked the beginning of the end for Eastern Europe's communist regimes.",
            "Dissolution of the USSR (December 1991): formally ended the Cold War.",
          ],
        },
      ],
    },
  },
  {
    slug: "guerras-mundiales",
    slugEn: "world-wars",
    emoji: "⚔️",
    category: "history",
    related: [
      "onu",
      "guerra-fria",
      "colonizacion-divisiones",
      "revolucion-rusa",
    ],
    es: {
      title: "Primera y Segunda Guerra Mundial",
      summary:
        "Los dos conflictos que redefinieron el mapa del siglo XX: causas, momentos clave y consecuencias.",
      sections: [
        {
          heading: "Primera Guerra Mundial: las causas de fondo",
          paragraphs: [
            "Para 1914, Europa estaba dividida en dos bloques de alianzas (la Triple Entente y la Triple Alianza) que convertían cualquier conflicto bilateral en uno potencialmente continental. A eso se sumaban un nacionalismo en ascenso, una carrera armamentística entre las grandes potencias y la competencia por colonias y mercados. El asesinato del archiduque Francisco Fernando de Austria-Hungría en Sarajevo, en junio de 1914, fue la chispa que encendió esa mezcla explosiva.",
          ],
        },
        {
          heading: "Primera Guerra Mundial: el desarrollo (1914-1918)",
          paragraphs: [
            "Enfrentó a los Aliados (Francia, Reino Unido, Rusia hasta 1917 y, desde 1917, Estados Unidos) contra las Potencias Centrales (Alemania, Austria-Hungría, el Imperio Otomano). Se caracterizó por la guerra de trincheras en el Frente Occidental —líneas casi estáticas durante años, con enormes bajas por batalla, como Verdún o el Somme— y por la aparición de armas nuevas: ametralladoras, gas venenoso, tanques y aviones de combate. Rusia se retiró del conflicto tras la Revolución de 1917, mientras la entrada de EE.UU. en ese mismo año inclinó la balanza a favor de los Aliados.",
          ],
        },
        {
          heading: "Primera Guerra Mundial: el final y sus consecuencias",
          paragraphs: [
            "Terminó con el armisticio del 11 de noviembre de 1918, tras casi 20 millones de muertos entre soldados y civiles. El Tratado de Versalles (1919) responsabilizó a Alemania por la guerra, le impuso fuertes reparaciones económicas y le quitó territorios y colonias. También creó la Sociedad de Naciones, antecesora de la ONU, que sin embargo no logró evitar un nuevo conflicto: el resentimiento alemán por Versalles fue uno de los factores que permitió el ascenso del nazismo.",
          ],
        },
        {
          heading: "Segunda Guerra Mundial: cómo se llegó a la guerra",
          paragraphs: [
            "En la Alemania humillada y empobrecida de posguerra, Adolf Hitler y el Partido Nazi llegaron al poder en 1933 prometiendo revertir Versalles. Italia, bajo Mussolini, y Japón, en plena expansión militar en Asia, compartían ambiciones territoriales similares. Las democracias europeas respondieron con una política de apaciguamiento —la más recordada es el Acuerdo de Múnich de 1938, que cedió territorio checoslovaco a Alemania a cambio de una promesa de paz que no se cumplió—, lo que solo le dio tiempo a Hitler para rearmarse.",
          ],
        },
        {
          heading: "Segunda Guerra Mundial: el desarrollo (1939-1945)",
          paragraphs: [
            "Comenzó el 1 de septiembre de 1939 con la invasión alemana a Polonia. Enfrentó a los Aliados (Reino Unido, la URSS desde junio de 1941, Estados Unidos desde diciembre de 1941, Francia y, entre muchos otros, China) contra el Eje (Alemania, Italia, Japón). Tuvo dos grandes escenarios: el europeo, con la invasión alemana a la URSS (Operación Barbarroja, 1941) y su punto de quiebre en la batalla de Stalingrad (1942-1943), y el del Pacífico, que se intensificó tras el ataque japonés a Pearl Harbor en diciembre de 1941. El desembarco aliado en Normandía (Día D, junio de 1944) abrió un segundo frente decisivo en Europa Occidental.",
          ],
        },
        {
          heading: "El Holocausto",
          paragraphs: [
            "En paralelo a los combates, la Alemania nazi llevó adelante un genocidio sistemático: el asesinato de unos seis millones de judíos europeos, junto con millones de romaníes, personas con discapacidad, prisioneros políticos y otros grupos perseguidos, en campos de concentración y exterminio. Es uno de los episodios más estudiados y documentados de la historia, y una referencia constante en el derecho internacional de los derechos humanos que se desarrolló después de la guerra.",
          ],
        },
        {
          heading: "Segunda Guerra Mundial: el final",
          paragraphs: [
            "Alemania se rindió el 8 de mayo de 1945 (Día de la Victoria en Europa), tras la caída de Berlín. En el Pacífico, Estados Unidos lanzó bombas atómicas sobre Hiroshima (6 de agosto) y Nagasaki (9 de agosto de 1945); Japón se rindió pocos días después, el 15 de agosto. Es el conflicto más letal de la historia: se estima que murieron entre 70 y 85 millones de personas, la mayoría civiles.",
          ],
        },
        {
          heading: "Consecuencias",
          list: [
            "Redibujó fronteras en Europa y Asia, y dividió a Alemania y a la ciudad de Berlín en zonas de ocupación.",
            "Dio origen a la Organización de las Naciones Unidas (ONU) en 1945, con el objetivo declarado de evitar una tercera guerra mundial.",
            "Los juicios de Núremberg (1945-1946) sentaron un precedente al juzgar a líderes nazis por crímenes de guerra y contra la humanidad.",
            "Marcó el inicio casi inmediato de la Guerra Fría entre EE.UU. y la URSS, los dos países que emergieron como superpotencias.",
            "Aceleró la descolonización de África y Asia: entre 1945 y mediados de la década de 1970, la gran mayoría de las colonias europeas se independizaron.",
          ],
        },
      ],
    },
    en: {
      title: "World War I and World War II",
      summary:
        "The two conflicts that redrew the map of the 20th century: causes, key turning points and aftermath.",
      sections: [
        {
          heading: "World War I: the underlying causes",
          paragraphs: [
            "By 1914, Europe was split into two alliance blocs (the Triple Entente and the Triple Alliance) that turned any bilateral dispute into a potentially continent-wide one. On top of that came rising nationalism, an arms race among the great powers, and competition over colonies and markets. The assassination of Archduke Franz Ferdinand of Austria-Hungary in Sarajevo, in June 1914, was the spark that set off that explosive mix.",
          ],
        },
        {
          heading: "World War I: how it unfolded (1914-1918)",
          paragraphs: [
            "It pitted the Allies (France, the United Kingdom, Russia until 1917, and the United States from 1917) against the Central Powers (Germany, Austria-Hungary, the Ottoman Empire). It's defined by trench warfare on the Western Front — nearly static lines for years, with enormous casualties per battle, as at Verdun or the Somme — and by the arrival of new weapons: machine guns, poison gas, tanks and combat aircraft. Russia withdrew from the war after the 1917 revolution, while the US joining that same year tipped the balance toward the Allies.",
          ],
        },
        {
          heading: "World War I: the end and its consequences",
          paragraphs: [
            "It ended with the armistice of November 11, 1918, after nearly 20 million deaths among soldiers and civilians. The Treaty of Versailles (1919) held Germany responsible for the war, imposed heavy economic reparations, and stripped it of territory and colonies. It also created the League of Nations, the UN's predecessor — which nonetheless failed to prevent a new conflict: German resentment over Versailles was one of the factors that enabled the rise of Nazism.",
          ],
        },
        {
          heading: "World War II: the road to war",
          paragraphs: [
            "In the humiliated, impoverished postwar Germany, Adolf Hitler and the Nazi Party rose to power in 1933 promising to reverse Versailles. Italy, under Mussolini, and Japan, in the middle of military expansion across Asia, shared similar territorial ambitions. European democracies responded with a policy of appeasement — most notably the 1938 Munich Agreement, which handed Czechoslovak territory to Germany in exchange for a promise of peace that wasn't kept — which only bought Hitler time to rearm.",
          ],
        },
        {
          heading: "World War II: how it unfolded (1939-1945)",
          paragraphs: [
            "It began on September 1, 1939, with Germany's invasion of Poland. It pitted the Allies (the United Kingdom, the USSR from June 1941, the United States from December 1941, France, and — among many others — China) against the Axis (Germany, Italy, Japan). It had two major theaters: the European one, with Germany's invasion of the USSR (Operation Barbarossa, 1941) and its turning point at the Battle of Stalingrad (1942-1943), and the Pacific one, which intensified after Japan's attack on Pearl Harbor in December 1941. The Allied landing in Normandy (D-Day, June 1944) opened a decisive second front in Western Europe.",
          ],
        },
        {
          heading: "The Holocaust",
          paragraphs: [
            "Alongside the fighting, Nazi Germany carried out a systematic genocide: the murder of some six million European Jews, together with millions of Roma people, people with disabilities, political prisoners and other persecuted groups, in concentration and extermination camps. It's one of the most studied and documented episodes in history, and a constant reference point in the international human rights law that developed after the war.",
          ],
        },
        {
          heading: "World War II: the end",
          paragraphs: [
            "Germany surrendered on May 8, 1945 (V-E Day), after the fall of Berlin. In the Pacific, the United States dropped atomic bombs on Hiroshima (August 6) and Nagasaki (August 9, 1945); Japan surrendered a few days later, on August 15. It's the deadliest conflict in history: an estimated 70 to 85 million people died, most of them civilians.",
          ],
        },
        {
          heading: "Aftermath",
          list: [
            "Redrew borders across Europe and Asia, and split Germany and the city of Berlin into occupation zones.",
            "Led to the founding of the United Nations (UN) in 1945, with the stated goal of preventing a third world war.",
            "The Nuremberg trials (1945-1946) set a precedent by prosecuting Nazi leaders for war crimes and crimes against humanity.",
            "Marked the near-immediate start of the Cold War between the US and the USSR, the two countries that emerged as superpowers.",
            "Accelerated decolonization across Africa and Asia: between 1945 and the mid-1970s, the vast majority of European colonies became independent.",
          ],
        },
      ],
    },
  },
  {
    slug: "otan",
    slugEn: "nato",
    emoji: "🛡️",
    category: "geopolitics",
    related: ["guerra-fria", "onu", "union-europea"],
    es: {
      title: "La OTAN: defensa colectiva y sus límites",
      summary:
        "Una alianza militar de 1949 que no dejó de crecer, y la cláusula que la define: invocada una sola vez en más de setenta años.",
      sections: [
        {
          heading: "Qué es y contra qué se creó",
          paragraphs: [
            "La Organización del Tratado del Atlántico Norte nació el 4 de abril de 1949, cuando doce países de América del Norte y Europa Occidental firmaron un tratado de defensa mutua frente a la Unión Soviética. No es un ejército propio: es un compromiso entre ejércitos nacionales, más una estructura de mando permanente para coordinarlos.",
            "La lógica es la de la disuasión. La alianza no busca ganar una guerra sino volverla tan costosa de iniciar que no empiece, y para eso necesita que el compromiso sea creíble antes de que alguien lo ponga a prueba.",
          ],
        },
        {
          heading: "El Artículo 5, y la única vez que se usó",
          paragraphs: [
            "El Artículo 5 establece que un ataque armado contra un miembro se considera un ataque contra todos. Es el corazón del tratado, y conviene leerlo con cuidado: obliga a cada país a asistir al atacado \"con la acción que juzgue necesaria\", lo que incluye la fuerza armada pero no la impone. La solidaridad es automática; la forma que toma, no.",
            "Se invocó una sola vez en toda la historia de la alianza, y no por una invasión en Europa: fue el 12 de septiembre de 2001, al día siguiente de los atentados contra Estados Unidos, y derivó en la operación en Afganistán. Que la única activación haya sido en favor del miembro más poderoso, y por un ataque no estatal, dice bastante sobre cuánto cambió el mundo respecto de 1949.",
          ],
        },
        {
          heading: "De doce miembros a treinta y dos",
          paragraphs: [
            "Tras el fin de la Guerra Fría la alianza se expandió hacia el este, incorporando a países que habían estado del otro lado: Polonia, Hungría y la República Checa en 1999, siete más en 2004. Finlandia se sumó en 2023 y Suecia en 2024, dos países que habían sostenido décadas de no alineamiento militar y lo abandonaron tras la invasión rusa a Ucrania.",
            "La ampliación es también el punto más discutido de su historia reciente. Para sus miembros, cada incorporación fue una decisión soberana de un país que pidió entrar. Para Rusia, el acercamiento sucesivo de la alianza a sus fronteras es la causa del conflicto, no su consecuencia. Entender el desacuerdo importa más que elegir un bando: es el eje de buena parte de la política europea actual.",
          ],
        },
        {
          heading: "Quién paga",
          paragraphs: [
            "En 2014 los miembros acordaron destinar al menos el 2% de su PBI a defensa. Durante una década la mayoría no lo cumplió, y el reparto del gasto fue una fuente constante de fricción con Estados Unidos, que aporta por lejos la mayor parte de la capacidad militar de la alianza. En 2025 el objetivo se elevó bastante por encima de ese 2%.",
            "La cifra explica una tensión de fondo: la defensa colectiva es lo que los economistas llaman un bien público. Protege a todos los miembros por igual, lo que le da a cada uno un incentivo para gastar menos y dejar que paguen los demás.",
          ],
        },
      ],
    },
    en: {
      title: "NATO: collective defence and its limits",
      summary:
        "A military alliance from 1949 that has never stopped growing, and the clause that defines it — invoked once in more than seventy years.",
      sections: [
        {
          heading: "What it is, and what it was built against",
          paragraphs: [
            "The North Atlantic Treaty Organization was founded on 4 April 1949, when twelve countries in North America and Western Europe signed a mutual defence treaty against the Soviet Union. It is not an army of its own: it is a commitment between national armies, plus a permanent command structure to coordinate them.",
            "The logic is deterrence. The alliance doesn't aim to win a war but to make starting one so costly that it never begins, and for that the commitment has to be credible before anyone tests it.",
          ],
        },
        {
          heading: "Article 5, and the one time it was used",
          paragraphs: [
            "Article 5 states that an armed attack against one member is considered an attack against all. It is the heart of the treaty, and it rewards careful reading: it obliges each country to assist the attacked member with \"such action as it deems necessary\", which includes armed force but does not require it. The solidarity is automatic; the shape it takes is not.",
            "It has been invoked once in the alliance's entire history, and not over an invasion in Europe: on 12 September 2001, the day after the attacks on the United States, leading to the operation in Afghanistan. That the only activation was on behalf of the most powerful member, and over a non-state attack, says a good deal about how far the world had moved from 1949.",
          ],
        },
        {
          heading: "From twelve members to thirty-two",
          paragraphs: [
            "After the Cold War ended the alliance expanded eastward, taking in countries that had been on the other side: Poland, Hungary and the Czech Republic in 1999, seven more in 2004. Finland joined in 2023 and Sweden in 2024, two countries that had held to decades of military non-alignment and abandoned it after Russia's invasion of Ukraine.",
            "Enlargement is also the most disputed part of its recent history. To its members, each accession was the sovereign decision of a country that asked to join. To Russia, the alliance's successive approach to its borders is the cause of the conflict rather than its consequence. Understanding the disagreement matters more than picking a side: it is the axis of much of European politics today.",
          ],
        },
        {
          heading: "Who pays",
          paragraphs: [
            "In 2014 members agreed to spend at least 2% of GDP on defence. For a decade most did not, and the split of the bill was a constant source of friction with the United States, which supplies by far the largest share of the alliance's military capability. In 2025 the target was raised well above that 2%.",
            "The figure explains an underlying tension: collective defence is what economists call a public good. It protects every member equally, which gives each one an incentive to spend less and let the others pay.",
          ],
        },
      ],
    },
  },
  {
    slug: "fmi",
    slugEn: "imf",
    emoji: "🏦",
    category: "development",
    related: ["pbi-idh", "capitalismo-y-alternativas", "primer-tercer-mundo"],
    es: {
      title: "El FMI y la deuda soberana: quién decide",
      summary:
        "Qué pasa cuando un país no puede pagar lo que debe, y por qué buena parte de la respuesta termina escribiéndose en Washington.",
      sections: [
        {
          heading: "No es un banco: es una caja entre socios",
          paragraphs: [
            "El Fondo Monetario Internacional se creó en 1944, en la conferencia de Bretton Woods, junto con el Banco Mundial. Hoy lo integran más de 190 países. Cada uno aporta una cuota, calculada según el tamaño de su economía, y de ese fondo común salen los préstamos. No capta depósitos ni cobra tasas de mercado: funciona más como una caja de auxilio entre socios que como un banco.",
            "El detalle decisivo es que la cuota no es solo lo que un país pone, sino lo que vale su voto. Estados Unidos tiene alrededor del 17% de las cuotas, y las decisiones más importantes requieren el 85% de los votos. La aritmética habla sola: ningún otro país llega a ese umbral solo, y Estados Unidos puede bloquear por sí mismo cualquiera de esas decisiones. Es la característica que más define al organismo y la que menos aparece cuando se lo discute.",
          ],
        },
        {
          heading: "Qué es, en concreto, un programa",
          paragraphs: [
            "Un país acude al FMI cuando no consigue las divisas para pagar sus importaciones y sus vencimientos de deuda —lo que se llama una crisis de balanza de pagos— y ya no puede o no quiere endeudarse en los mercados. El Fondo presta, pero no de una vez: desembolsa por tramos, y cada tramo depende de que se hayan cumplido las metas acordadas.",
            "Esas metas son la \"condicionalidad\", y son el núcleo de toda la discusión. Suelen incluir objetivos de déficit fiscal, de reservas y de emisión monetaria, y a veces reformas estructurales: impuestos, subsidios, jubilaciones, empresas públicas. Un programa del FMI no es solo dinero prestado; es un acuerdo sobre qué va a hacer un país con su economía durante los años siguientes.",
          ],
        },
        {
          heading: "La discusión que lleva cuarenta años",
          paragraphs: [
            "Quienes defienden la condicionalidad plantean un problema real: si el Fondo prestara sin condiciones, estaría cubriendo las consecuencias de políticas que llevaron a la crisis y haciendo más probable la siguiente. Es lo que en economía se llama riesgo moral. Además, el dinero prestado es de todos los socios, y el organismo tiene que poder recuperarlo.",
            "Quienes la critican señalan que el ajuste no cae parejo. Durante los años ochenta y noventa, los programas exigieron recortes de gasto público —incluidos salud y educación— como condición para desembolsar, y en varios países agravaron la pobreza en el corto plazo aunque buscaran estabilizar a largo plazo. La objeción más de fondo no es sobre los números sino sobre la legitimidad: quien decide qué se recorta no es quien vota en ese país.",
            "Lo que casi nunca se menciona es que el propio Fondo revisó parte de su posición. Desde 2010, investigaciones publicadas por su departamento de estudios cuestionaron la eficacia de la austeridad en recesión y los beneficios de liberalizar los flujos de capital sin restricciones, dos pilares de la receta anterior. El organismo cambió de opinión sobre sí mismo, y eso también es parte de la historia.",
          ],
        },
        {
          heading: "Tres casos que explican el resto",
          paragraphs: [
            "Asia, 1997. Tailandia, Indonesia y Corea del Sur recibieron programas con condiciones severas durante una crisis financiera regional. Las economías se recuperaron, pero el costo social y el resentimiento fueron tales que varios países asiáticos acumularon desde entonces enormes reservas para no tener que volver a pedir.",
            "Grecia, 2010-2018. Tres programas seguidos, negociados junto con la Comisión Europea y el Banco Central Europeo. La economía griega se contrajo alrededor de un cuarto y el desempleo superó el 25%. El propio FMI reconoció después que había subestimado cuánto daña el ajuste a una economía que no puede devaluar su moneda.",
            "Argentina, 2018. El acuerdo más grande de la historia del organismo, de unos 57.000 millones de dólares. Sigue siendo objeto de disputa dentro y fuera del país: sobre si el monto era razonable, sobre para qué se usaron los fondos y sobre la responsabilidad compartida entre quien pidió y quien prestó.",
          ],
        },
        {
          heading: "Por qué importa quién manda",
          paragraphs: [
            "Desde 1944 rige una convención no escrita: la dirección del FMI la ocupa siempre un europeo y la presidencia del Banco Mundial un estadounidense. Nunca fue una regla formal, y se sostiene igual.",
            "El reparto de cuotas se revisó en 2010 para dar más peso a las economías emergentes, y China ganó participación, pero el orden general se mantuvo. Por eso la pregunta que abre este tema —quién decide— no es retórica. Un país en crisis negocia su política económica con un organismo cuyo poder de voto refleja el tamaño de las economías de 1944 mucho más de lo que refleja el mundo de hoy.",
          ],
        },
      ],
    },
    en: {
      title: "The IMF and sovereign debt: who decides",
      summary:
        "What happens when a country cannot pay what it owes, and why much of the answer ends up being written in Washington.",
      sections: [
        {
          heading: "Not a bank: a fund between members",
          paragraphs: [
            "The International Monetary Fund was created in 1944 at the Bretton Woods conference, alongside the World Bank. More than 190 countries belong to it today. Each pays in a quota, calculated from the size of its economy, and the loans come out of that common pool. It takes no deposits and charges no market rates: it works more like an emergency fund between members than like a bank.",
            "The decisive detail is that a quota is not only what a country puts in — it is what its vote is worth. The United States holds around 17% of quotas, and the most important decisions require 85% of the votes. The arithmetic speaks for itself: no other country reaches that threshold alone, and the United States can block any of those decisions by itself. It is the feature that defines the institution most, and the one that comes up least when it is argued about.",
          ],
        },
        {
          heading: "What a programme actually is",
          paragraphs: [
            "A country goes to the IMF when it cannot get the foreign currency to pay for imports and maturing debt — a balance-of-payments crisis — and can no longer, or will no longer, borrow on the markets. The Fund lends, but not in one go: it disburses in tranches, and each tranche depends on the agreed targets having been met.",
            "Those targets are \"conditionality\", and they are the core of the whole argument. They usually include goals for the fiscal deficit, reserves and money creation, and sometimes structural reforms: taxes, subsidies, pensions, state-owned companies. An IMF programme is not just money lent; it is an agreement about what a country will do with its economy for years afterwards.",
          ],
        },
        {
          heading: "The argument that has run for forty years",
          paragraphs: [
            "Those who defend conditionality point to a real problem: lending without conditions would cover the consequences of the policies that caused the crisis and make the next one more likely. Economists call it moral hazard. The money lent also belongs to all the members, and the institution has to be able to get it back.",
            "Those who criticise it point out that the adjustment does not fall evenly. Through the 1980s and 1990s, programmes required cuts to public spending — health and education included — as a condition for disbursement, and in several countries they deepened poverty in the short term even as they aimed to stabilise in the long run. The deeper objection is not about the numbers but about legitimacy: whoever decides what gets cut is not whoever votes in that country.",
            "What is almost never mentioned is that the Fund revisited part of its own position. From 2010 onwards, research published by its own economists questioned the effectiveness of austerity in a recession and the benefits of unrestricted capital flows, two pillars of the earlier prescription. The institution changed its mind about itself, and that is part of the story too.",
          ],
        },
        {
          heading: "Three cases that explain the rest",
          paragraphs: [
            "Asia, 1997. Thailand, Indonesia and South Korea took programmes with severe conditions during a regional financial crisis. The economies recovered, but the social cost and the resentment were such that several Asian countries have piled up enormous reserves ever since, so as never to have to ask again.",
            "Greece, 2010-2018. Three consecutive programmes, negotiated together with the European Commission and the European Central Bank. The Greek economy shrank by roughly a quarter and unemployment passed 25%. The IMF itself later acknowledged that it had underestimated how much austerity damages an economy that cannot devalue its own currency.",
            "Argentina, 2018. The largest agreement in the institution's history, around 57 billion dollars. It remains disputed inside and outside the country: over whether the amount was reasonable, over what the funds were used for, and over the responsibility shared between the country that asked and the institution that lent.",
          ],
        },
        {
          heading: "Why it matters who is in charge",
          paragraphs: [
            "An unwritten convention has held since 1944: a European always heads the IMF and an American always presides over the World Bank. It was never a formal rule, and it holds anyway.",
            "Quotas were revised in 2010 to give emerging economies more weight, and China gained a share, but the overall order held. That is why the question this topic opens with — who decides — is not rhetorical. A country in crisis negotiates its economic policy with an institution whose voting power reflects the size of the economies of 1944 far more than it reflects the world of today.",
          ],
        },
      ],
    },
  },
  {
    slug: "colonizacion-divisiones",
    slugEn: "colonisation-and-divisions",
    emoji: "🗺️",
    category: "geopolitics",
    related: [
      "esclavitud-comercio-triangular",
      "independencia-india-particion",
      "union-africana-panafricanismo",
      "idiomas-del-mundo",
    ],
    es: {
      title: "Colonización y divisiones geopolíticas",
      summary:
        "Por qué el mundo está dividido en países, y esos países en provincias, estados o territorios.",
      sections: [
        {
          heading: "Qué fue la colonización",
          paragraphs: [
            "Entre los siglos XV y XX, potencias europeas (España, Portugal, Reino Unido, Francia, Países Bajos, Bélgica, entre otras) ocuparon y gobernaron territorios en América, África, Asia y Oceanía, extrayendo recursos e imponiendo su idioma, religión y sistema administrativo.",
          ],
        },
        {
          heading: "Por qué existen divisiones geopolíticas",
          paragraphs: [
            "La forma en que un país se organiza internamente —en provincias, estados, departamentos o territorios— responde a una combinación de factores:",
          ],
          list: [
            "Sistema de gobierno: los países federales (Argentina, Estados Unidos, Alemania) reparten el poder entre estados y nación; los centralizados (Francia) concentran el poder en el gobierno nacional.",
            "Historia y colonización: muchas divisiones heredan el sistema del país colonizador, como las provincias canadienses de origen británico.",
            "Geografía: montañas, ríos o desiertos suelen marcar límites administrativos.",
            "Tamaño y población: países muy grandes (Brasil, India) se subdividen para poder gobernarse; los más chicos usan solo ciudades o municipios.",
            "Cultura y etnicidad: algunas divisiones dan representación a grupos étnicos o culturales, como las Comunidades Autónomas de España.",
            "La constitución de cada país define quién tiene autoridad sobre cada división.",
          ],
        },
        {
          heading: "El legado de las fronteras coloniales",
          paragraphs: [
            "Muchas fronteras actuales, sobre todo en África y Medio Oriente, fueron trazadas por potencias coloniales sin considerar las divisiones étnicas o culturales preexistentes. Ese desajuste explica buena parte de los conflictos geopolíticos actuales.",
          ],
        },
        {
          heading: "La ola de descolonización",
          paragraphs: [
            "La mayoría de las colonias del mundo se independizaron en apenas tres décadas: India y Pakistán en 1947, gran parte del sudeste asiático en los años 50, y la ola más grande en África, donde seis países se independizaron en el solo \"Año de África\" (1960). El proceso continuó hasta mediados de los años 70, cuando Portugal —la última gran potencia colonial en resistirse— cedió la independencia a Angola, Mozambique y otras colonias africanas.",
            "No siempre fue pacífico: guerras de independencia como la de Argelia contra Francia (1954-1962) dejaron cientos de miles de muertos, y varias fronteras heredadas de la colonización siguieron generando conflictos internos y entre países durante décadas después.",
          ],
        },
      ],
    },
    en: {
      title: "Colonization and geopolitical divisions",
      summary:
        "Why the world is split into countries, and those countries into provinces, states or territories.",
      sections: [
        {
          heading: "What colonization was",
          paragraphs: [
            "Between the 15th and 20th centuries, European powers (Spain, Portugal, the United Kingdom, France, the Netherlands, Belgium, among others) occupied and ruled territories across the Americas, Africa, Asia and Oceania, extracting resources and imposing their language, religion and administrative system.",
          ],
        },
        {
          heading: "Why geopolitical divisions exist",
          paragraphs: [
            "How a country organizes itself internally — into provinces, states, departments or territories — comes down to a mix of factors:",
          ],
          list: [
            "System of government: federal countries (Argentina, the US, Germany) split power between states and the nation; centralized ones (France) keep power in the national government.",
            "History and colonization: many divisions inherit the colonizing country's system, like Canada's British-derived provinces.",
            "Geography: mountains, rivers or deserts often mark administrative boundaries.",
            "Size and population: very large countries (Brazil, India) subdivide to stay governable; smaller ones may need only cities or municipalities.",
            "Culture and ethnicity: some divisions give representation to ethnic or cultural groups, like Spain's Autonomous Communities.",
            "Each country's constitution defines who holds authority over each division.",
          ],
        },
        {
          heading: "The legacy of colonial borders",
          paragraphs: [
            "Many current borders, especially in Africa and the Middle East, were drawn by colonial powers without regard for pre-existing ethnic or cultural divisions. That mismatch explains a good share of today's geopolitical conflicts.",
          ],
        },
        {
          heading: "The wave of decolonization",
          paragraphs: [
            "Most of the world's colonies became independent in barely three decades: India and Pakistan in 1947, much of Southeast Asia in the 1950s, and the biggest wave in Africa, where six countries gained independence in the single \"Year of Africa\" (1960). The process continued into the mid-1970s, when Portugal — the last major colonial power still holding on — granted independence to Angola, Mozambique and other African colonies.",
            "It wasn't always peaceful: independence wars like Algeria's against France (1954-1962) left hundreds of thousands dead, and several borders inherited from colonization kept fueling conflicts within and between countries for decades afterward.",
          ],
        },
      ],
    },
  },
  {
    slug: "pbi-idh",
    slugEn: "gdp-and-hdi",
    emoji: "📊",
    category: "development",
    related: ["primer-tercer-mundo", "cambio-climatico", "capitalismo-y-alternativas"],
    es: {
      title: "PBI e IDH: cómo se mide el desarrollo",
      summary:
        "Dos indicadores muy citados —y por qué no siempre cuentan la misma historia.",
      sections: [
        {
          heading: "PBI (Producto Bruto Interno)",
          paragraphs: [
            "Es el valor total de los bienes y servicios producidos por un país en un período determinado. El PBI per cápita (dividido por la población) se usa para comparar el tamaño económico entre países, pero no dice nada sobre cómo se distribuye esa riqueza entre sus habitantes.",
          ],
        },
        {
          heading: "IDH (Índice de Desarrollo Humano)",
          paragraphs: [
            "Creado por el PNUD (Programa de las Naciones Unidas para el Desarrollo), combina tres dimensiones: esperanza de vida al nacer (salud), años de escolarización (educación) y el ingreso nacional bruto per cápita (nivel de vida). Va de 0 a 1: cuanto más cerca de 1, mayor desarrollo humano.",
            "El IDH es una herramienta importante para evaluar el bienestar y la calidad de vida de las personas en diferentes países, complementando medidas puramente económicas como el PBI.",
          ],
        },
        {
          heading: "Por qué no siempre coinciden",
          paragraphs: [
            "Un país puede tener un PBI per cápita muy alto —por ejemplo, gracias a la exportación de petróleo u otro recurso natural— sin que esa riqueza se traduzca en mejor salud o educación para el conjunto de la población, si está concentrada en pocas manos o el Estado no invierte lo suficiente en servicios públicos. En ese caso, el PBI sube mientras el IDH queda rezagado.",
            "A la inversa, un país con un PBI per cápita moderado puede alcanzar un IDH alto si invierte de forma sostenida en salud pública y educación universal, logrando una esperanza de vida larga y buenos niveles de escolarización incluso sin un ingreso excepcional.",
            "Por eso el PNUD creó el IDH: para mostrar que el desarrollo de un país no se puede resumir en un solo número económico, y que dos países con un PBI similar pueden ofrecerle a sus habitantes una calidad de vida muy distinta.",
          ],
        },
        {
          heading: "Otros índices relacionados",
          paragraphs: [
            "El IDH tiene primos que agregan matices que el PBI no captura:",
          ],
          list: [
            "IDH Ajustado por Desigualdad (IDHD): corrige el IDH según cuán desigual es la distribución del desarrollo dentro del país.",
            "Índice de Pobreza Multidimensional (IPM): mide la pobreza considerando educación, salud y nivel de vida, no solo ingresos.",
            "Índice de Desigualdad de Género (IDG): mide la brecha entre hombres y mujeres en salud, empoderamiento y trabajo.",
          ],
        },
      ],
    },
    en: {
      title: "GDP and HDI: how development gets measured",
      summary:
        "Two widely cited indicators — and why they don't always tell the same story.",
      sections: [
        {
          heading: "GDP (Gross Domestic Product)",
          paragraphs: [
            "GDP is the total value of goods and services a country produces in a given period. GDP per capita (divided by population) is used to compare economic size across countries, but says nothing about how that wealth is distributed among its people.",
          ],
        },
        {
          heading: "HDI (Human Development Index)",
          paragraphs: [
            "Created by the UNDP (United Nations Development Programme), it combines three dimensions: life expectancy at birth (health), years of schooling (education), and gross national income per capita (standard of living). It runs from 0 to 1: the closer to 1, the higher the human development.",
            "The HDI is an important tool for evaluating people's well-being and quality of life across countries, complementing purely economic measures like GDP.",
          ],
        },
        {
          heading: "Why they don't always agree",
          paragraphs: [
            "A country can have a very high GDP per capita — say, from exporting oil or another natural resource — without that wealth translating into better health or education for the population as a whole, if it's concentrated among a few or the state doesn't invest enough in public services. In that case, GDP climbs while HDI lags behind.",
            "Conversely, a country with a modest GDP per capita can reach a high HDI by investing steadily in public health and universal education, achieving long life expectancy and strong schooling levels even without an exceptional income.",
            "That's exactly why the UNDP created the HDI: to show that a country's development can't be summed up in a single economic number, and that two countries with similar GDP can offer their people very different qualities of life.",
          ],
        },
        {
          heading: "Related indices",
          paragraphs: [
            "The HDI has relatives that capture nuances GDP misses:",
          ],
          list: [
            "Inequality-adjusted HDI (IHDI): discounts the HDI based on how unevenly development is distributed within the country.",
            "Multidimensional Poverty Index (MPI): measures poverty across education, health and living standards, not just income.",
            "Gender Inequality Index (GII): measures the gap between men and women in health, empowerment and work.",
          ],
        },
      ],
    },
  },
  {
    slug: "monarquias",
    slugEn: "monarchies",
    emoji: "👑",
    category: "geopolitics",
    related: [
      "commonwealth",
      "estado-nacion-westfalia",
      "democracia-autocracia-sistemas-gobierno",
    ],
    es: {
      title: "Monarquías del mundo",
      summary:
        "44 países todavía tienen un rey, reina o emperador como jefe de Estado. Así se dividen.",
      sections: [
        {
          heading: "¿Qué es una monarquía?",
          paragraphs: [
            "Es un sistema de gobierno donde un monarca (rey, reina, emperador, sultán, emir) es el jefe de Estado, generalmente de forma hereditaria y de por vida. Hoy existen 44 países con algún tipo de monarquía.",
          ],
        },
        {
          heading: "Monarquías absolutas",
          paragraphs: [
            "El monarca gobierna con control prácticamente total sobre el Estado, sin un parlamento con poder real que lo limite.",
          ],
          list: ["Arabia Saudita", "Brunéi", "Esuatini (antigua Suazilandia)", "Omán"],
        },
        {
          heading: "Monarquías constitucionales",
          paragraphs: [
            "El poder del monarca está limitado por una constitución y, en la mayoría de los casos, un parlamento; el jefe de gobierno suele ser un primer ministro y no hay un presidente. Es el tipo de monarquía más común, con 36 países:",
          ],
          list: [
            "Andorra (dos copríncipes: el presidente de Francia y el obispo de Urgel, España)",
            "Australia",
            "Bahamas",
            "Bahréin",
            "Barbados",
            "Bélgica",
            "Belice",
            "Bután",
            "Camboya",
            "Canadá",
            "Dinamarca",
            "Emiratos Árabes Unidos (combina un presidente nacional con monarcas locales —emires— en cada emirato)",
            "España",
            "Granada",
            "Jamaica",
            "Japón",
            "Jordania",
            "Kuwait",
            "Lesoto",
            "Liechtenstein",
            "Luxemburgo",
            "Malasia (combina un rey nacional con monarcas locales —sultanes— en varios de sus estados)",
            "Marruecos",
            "Mónaco",
            "Noruega",
            "Nueva Zelanda",
            "Países Bajos",
            "Papúa Nueva Guinea",
            "Qatar",
            "Reino Unido",
            "San Cristóbal y Nieves",
            "Santa Lucía",
            "San Vicente y las Granadinas",
            "Suecia",
            "Tailandia",
            "Tonga",
          ],
        },
        {
          heading: "Monarquías con gobernador general",
          paragraphs: [
            "En varios países de la Commonwealth —Australia, Canadá, Nueva Zelanda, Jamaica—, el rey del Reino Unido es jefe de Estado y está representado localmente por un gobernador general, mientras un primer ministro lidera el día a día del gobierno.",
          ],
        },
        {
          heading: "Un caso único: monarquía electiva",
          paragraphs: [
            "La Ciudad del Vaticano es una monarquía electiva: el Papa actúa como jefe de Estado, pero es elegido por el Colegio Cardenalicio en lugar de heredar el cargo.",
          ],
        },
      ],
    },
    en: {
      title: "Monarchies around the world",
      summary:
        "44 countries still have a king, queen or emperor as head of state. Here's how they break down.",
      sections: [
        {
          heading: "What is a monarchy?",
          paragraphs: [
            "A system of government where a monarch (king, queen, emperor, sultan, emir) is the head of state, usually hereditary and for life. There are 44 countries with some form of monarchy today.",
          ],
        },
        {
          heading: "Absolute monarchies",
          paragraphs: [
            "The monarch holds near-total control over the state, without a parliament with real power to limit them.",
          ],
          list: ["Saudi Arabia", "Brunei", "Eswatini (formerly Swaziland)", "Oman"],
        },
        {
          heading: "Constitutional monarchies",
          paragraphs: [
            "The monarch's power is limited by a constitution and, in most cases, a parliament; the head of government is typically a prime minister, with no president. It's the most common type of monarchy, with 36 countries:",
          ],
          list: [
            "Andorra (two co-princes: the President of France and the Bishop of Urgell, Spain)",
            "Australia",
            "the Bahamas",
            "Bahrain",
            "Barbados",
            "Belgium",
            "Belize",
            "Bhutan",
            "Cambodia",
            "Canada",
            "Denmark",
            "the UAE (combines a national president with local monarchs — emirs — in each emirate)",
            "Spain",
            "Grenada",
            "Jamaica",
            "Japan",
            "Jordan",
            "Kuwait",
            "Lesotho",
            "Liechtenstein",
            "Luxembourg",
            "Malaysia (combines a national king with local monarchs — sultans — in several of its states)",
            "Morocco",
            "Monaco",
            "Norway",
            "New Zealand",
            "the Netherlands",
            "Papua New Guinea",
            "Qatar",
            "the UK",
            "Saint Kitts and Nevis",
            "Saint Lucia",
            "Saint Vincent and the Grenadines",
            "Sweden",
            "Thailand",
            "Tonga",
          ],
        },
        {
          heading: "Monarchies with a governor-general",
          paragraphs: [
            "In several Commonwealth countries — Australia, Canada, New Zealand, Jamaica — the British monarch is head of state and is represented locally by a governor-general, while a prime minister runs day-to-day government.",
          ],
        },
        {
          heading: "A unique case: elective monarchy",
          paragraphs: [
            "Vatican City is an elective monarchy: the Pope acts as head of state but is elected by the College of Cardinals rather than inheriting the role.",
          ],
        },
      ],
    },
  },
  {
    slug: "commonwealth",
    slugEn: "commonwealth",
    emoji: "🌐",
    category: "geopolitics",
    related: [
      "colonizacion-divisiones",
      "monarquias",
      "independencia-india-particion",
    ],
    es: {
      title: "La Commonwealth: qué es y quiénes la integran",
      summary:
        "56 países cooperan en esta asociación voluntaria, aunque la mayoría ya no tiene al monarca británico como jefe de Estado.",
      sections: [
        {
          heading: "Qué es",
          paragraphs: [
            "La Mancomunidad de Naciones (Commonwealth of Nations) es una asociación voluntaria de 56 países, la mayoría antiguas colonias o territorios del Imperio Británico, que cooperan en temas de comercio, democracia y desarrollo.",
          ],
        },
        {
          heading: "De imperio a asociación voluntaria",
          paragraphs: [
            "La Declaración Balfour de 1926 y el Estatuto de Westminster de 1931 reconocieron a los dominios del Imperio Británico (Canadá, Australia, Nueva Zelanda, Sudáfrica e Irlanda) como naciones autónomas e iguales al Reino Unido, unidas por lealtad compartida a la corona: ahí nació la Commonwealth moderna. La Declaración de Londres de 1949 dio el paso final, al permitir que India se quedara como miembro pese a convertirse en república sin el monarca británico como jefe de Estado, abriendo la puerta a que la mayoría de los miembros actuales sean repúblicas.",
          ],
        },
        {
          heading: "El rol del monarca británico",
          paragraphs: [
            "El rey del Reino Unido es la \"Cabeza de la Commonwealth\", un rol simbólico. Solo 14 de sus miembros —los llamados \"Reinos de la Mancomunidad\", como Canadá, Australia o Jamaica— lo reconocen además como su propio jefe de Estado. La mayoría de los miembros son repúblicas independientes (India, Sudáfrica, Nigeria, entre otras) que no tienen al monarca como jefe de Estado.",
          ],
        },
        {
          heading: "No hace falta haber sido colonia",
          paragraphs: [
            "Países como Mozambique y Ruanda se unieron a la Commonwealth sin haber sido colonias británicas, lo que muestra que hoy funciona más como un club de cooperación voluntaria que como un vestigio del imperio.",
          ],
        },
        {
          heading: "Qué hace en la práctica",
          paragraphs: [
            "Más allá del símbolo, la Commonwealth organiza cada dos años los Juegos de la Commonwealth (una versión reducida de los Juegos Olímpicos solo entre sus miembros) y la Reunión de Jefes de Gobierno (CHOGM), donde se coordinan posiciones sobre comercio, cambio climático y democracia. Su secretaría, con sede en Londres, también financia programas de desarrollo y observa elecciones en países miembros.",
          ],
        },
      ],
    },
    en: {
      title: "The Commonwealth: what it is and who's in it",
      summary:
        "56 countries cooperate in this voluntary association, though most no longer have the British monarch as head of state.",
      sections: [
        {
          heading: "What it is",
          paragraphs: [
            "The Commonwealth of Nations is a voluntary association of 56 countries, most of them former colonies or territories of the British Empire, that cooperate on trade, democracy and development.",
          ],
        },
        {
          heading: "From empire to voluntary association",
          paragraphs: [
            "The 1926 Balfour Declaration and the 1931 Statute of Westminster recognized the British Empire's dominions (Canada, Australia, New Zealand, South Africa and Ireland) as self-governing nations equal to the UK, united by shared loyalty to the crown — that's where the modern Commonwealth was born. The 1949 London Declaration took the final step, letting India stay a member even after becoming a republic without the British monarch as head of state, opening the door for most of today's members to be republics.",
          ],
        },
        {
          heading: "The British monarch's role",
          paragraphs: [
            "The British monarch is the \"Head of the Commonwealth\", a symbolic role. Only 14 of its members — the so-called \"Commonwealth realms\", like Canada, Australia or Jamaica — also recognize them as their own head of state. Most members are independent republics (India, South Africa, Nigeria, among others) that don't have the monarch as head of state.",
          ],
        },
        {
          heading: "You don't need a colonial past",
          paragraphs: [
            "Countries like Mozambique and Rwanda joined the Commonwealth without ever being British colonies, showing it now works more like a voluntary cooperation club than a leftover of empire.",
          ],
        },
        {
          heading: "What it actually does",
          paragraphs: [
            "Beyond the symbolism, the Commonwealth holds the Commonwealth Games every four years (a scaled-down Olympics just among its members) and the Commonwealth Heads of Government Meeting (CHOGM), where members coordinate positions on trade, climate change and democracy. Its secretariat, based in London, also funds development programs and observes elections in member countries.",
          ],
        },
      ],
    },
  },
  {
    slug: "datos-na",
    slugEn: "missing-data",
    emoji: "❓",
    category: "reading-data",
    related: ["pbi-idh", "cuantos-paises-hay"],
    es: {
      title: "¿Qué significa \"N/D\" en los datos de un país?",
      summary:
        "Por qué algunos países no tienen un valor para ciertos indicadores, y qué no significa esa ausencia.",
      sections: [
        {
          heading: "El significado literal",
          paragraphs: [
            "\"N/D\" (o \"N/A\" en inglés, de Not Available / Not Applicable) aparece cuando un indicador no se pudo calcular o no fue medido para ese territorio.",
          ],
        },
        {
          heading: "Por qué pasa más con territorios pequeños",
          paragraphs: [
            "Organismos como el Banco Mundial, la ONU o el PNUD suelen publicar ciertos indicadores —como la tasa de pobreza o el IDH— solo para países soberanos con estadísticas nacionales propias. Territorios sin población permanente, como las Tierras Australes y Antárticas Francesas, o dependencias administradas por otro país, muchas veces no generan esos datos de forma independiente.",
          ],
        },
        {
          heading: "No es lo mismo que \"cero\"",
          paragraphs: [
            "Un dato faltante no significa que el valor real sea cero o insignificante: significa que no existe una medición confiable disponible. Es una distinción importante al comparar países entre sí.",
          ],
        },
        {
          heading: "No todos los organismos miden lo mismo",
          paragraphs: [
            "Además, distintos organismos cubren distintos países: el Banco Mundial suele incluir más territorios dependientes que el PNUD, y algunos países directamente no comparten cierta información con organismos internacionales por decisión propia. Por eso es común encontrar un dato disponible para un país en una fuente y ausente en otra.",
          ],
        },
      ],
    },
    en: {
      title: "What does \"N/A\" mean in a country's data?",
      summary:
        "Why some countries have no value for certain indicators, and what that absence doesn't mean.",
      sections: [
        {
          heading: "The literal meaning",
          paragraphs: [
            "\"N/A\" (Not Available / Not Applicable) shows up when an indicator couldn't be calculated or wasn't measured for that territory.",
          ],
        },
        {
          heading: "Why it happens more with small territories",
          paragraphs: [
            "Bodies like the World Bank, the UN or the UNDP often publish certain indicators — like the poverty rate or the HDI — only for sovereign countries with their own national statistics. Territories with no permanent population, like the French Southern and Antarctic Lands, or dependencies administered by another country, often don't generate that data independently.",
          ],
        },
        {
          heading: "It's not the same as \"zero\"",
          paragraphs: [
            "A missing value doesn't mean the real figure is zero or insignificant — it means no reliable measurement is available. That's an important distinction when comparing countries.",
          ],
        },
        {
          heading: "Not every organization measures the same things",
          paragraphs: [
            "On top of that, different bodies cover different territories: the World Bank tends to include more dependent territories than the UNDP, and some countries simply choose not to share certain data with international organizations. That's why it's common to find a figure available for a country in one source and missing in another.",
          ],
        },
      ],
    },
  },
  {
    slug: "cuantos-paises-hay",
    slugEn: "how-many-countries",
    emoji: "🔢",
    category: "reading-data",
    related: ["estado-nacion-westfalia", "onu"],
    es: {
      title: "¿Cuántos países hay en el mundo?",
      summary:
        "La ONU tiene 193 miembros, pero la respuesta cambia según a quién le preguntes.",
      sections: [
        {
          heading: "Según la ONU",
          paragraphs: [
            "La Organización de las Naciones Unidas tiene 193 estados miembros. Desde su fundación en 1945, ese número creció de los 51 países originales hasta hoy; Sudán del Sur, en 2011, fue el más reciente en incorporarse.",
          ],
        },
        {
          heading: "Los estados observadores",
          paragraphs: [
            "Palestina y la Santa Sede (Ciudad del Vaticano) son \"estados observadores no miembros\": pueden participar y hablar en la Asamblea General, pero no votar. Sumados a los 193 miembros, dan un total de 195.",
          ],
        },
        {
          heading: "Por qué la cifra varía",
          paragraphs: [
            "El reconocimiento de un territorio como país \"soberano\" depende de decisiones políticas de cada estado. No todos los miembros de la ONU reconocen a Palestina como estado propio, por lo que distintas fuentes hablan de 193, 194 o 195 según el criterio usado.",
          ],
        },
        {
          heading: "Otros territorios con reconocimiento disputado",
          paragraphs: [
            "Palestina no es el único caso. Taiwán se gobierna de forma independiente desde 1949, pero solo alrededor de una decena de países lo reconocen como estado propio, ya que la mayoría respeta la postura de China de que forma parte de su territorio. Kosovo declaró su independencia de Serbia en 2008 y fue reconocido por más de 100 países, pero no por Serbia, Rusia, China ni varios miembros de la Unión Europea. El Sahara Occidental es reclamado tanto por Marruecos como por el Frente Polisario, que proclamó ahí la República Árabe Saharaui Democrática. Ninguno de estos tres es miembro de la ONU, aunque los tres tienen gobierno, territorio y población propios.",
          ],
        },
      ],
    },
    en: {
      title: "How many countries are there in the world?",
      summary:
        "The UN has 193 members, but the answer shifts depending on who you ask.",
      sections: [
        {
          heading: "According to the UN",
          paragraphs: [
            "The United Nations has 193 member states. Since it was founded in 1945, that number grew from the original 51 countries to today's total; South Sudan, in 2011, was the most recent to join.",
          ],
        },
        {
          heading: "The observer states",
          paragraphs: [
            "Palestine and the Holy See (Vatican City) are \"non-member observer states\": they can take part and speak at the General Assembly, but can't vote. Added to the 193 members, that makes 195.",
          ],
        },
        {
          heading: "Why the number shifts",
          paragraphs: [
            "Recognizing a territory as a \"sovereign\" country depends on each state's own political decisions. Not every UN member recognizes Palestine as its own state, so different sources cite 193, 194 or 195 depending on the criteria used.",
          ],
        },
        {
          heading: "Other territories with disputed recognition",
          paragraphs: [
            "Palestine isn't the only case. Taiwan has governed itself independently since 1949, but only around a dozen countries recognize it as its own state, since most follow China's position that it's part of Chinese territory. Kosovo declared independence from Serbia in 2008 and has been recognized by more than 100 countries, but not by Serbia, Russia, China or several EU members. Western Sahara is claimed both by Morocco and by the Polisario Front, which proclaimed the Sahrawi Arab Democratic Republic there. None of these three is a UN member, even though all three have their own government, territory and population.",
          ],
        },
      ],
    },
  },
  {
    slug: "religiones-del-mundo",
    slugEn: "world-religions",
    emoji: "🕊️",
    category: "society",
    related: [
      "eeuu-medio-oriente",
      "colonizacion-divisiones",
      "independencia-india-particion",
    ],
    es: {
      title: "Religiones del mundo",
      summary:
        "Cuáles son las religiones más practicadas, cómo se distribuyen y por qué le importan a la geopolítica.",
      sections: [
        {
          heading: "Las más practicadas",
          paragraphs: [
            "Según estimaciones demográficas globales, el cristianismo es la religión con más adherentes (unos 2.400 millones), seguido por el islam (unos 1.900 millones). Les siguen el hinduismo (unos 1.200 millones, mayoritario en la India), el budismo (unos 500 millones, concentrado en el este y sudeste asiático) y las religiones populares o tradicionales, practicadas por cientos de millones de personas sobre todo en África y Asia. El judaísmo tiene unos 15 millones de adherentes en el mundo, una cifra pequeña frente a su enorme peso histórico y cultural. Además, se estima que más de 1.000 millones de personas no se identifican con ninguna religión.",
          ],
        },
        {
          heading: "Cómo se distribuyen geográficamente",
          paragraphs: [
            "La religión dominante de un país suele reflejar su historia: el cristianismo se expandió por América, África subsahariana y Oceanía en buena parte a través de la colonización europea; el islam se extendió por el norte de África, Medio Oriente y el sur y sudeste asiático a través del comercio y de conquistas sucesivas desde el siglo VII; el hinduismo y el budismo son originarios del subcontinente indio, desde donde el budismo se expandió hacia el este asiático a través de rutas comerciales y misioneros.",
          ],
        },
        {
          heading: "Religión y Estado",
          paragraphs: [
            "Muchos países tienen una religión oficial o de Estado: el islam en Arabia Saudita e Irán, el budismo en Tailandia y Bhután, o el cristianismo anglicano en el Reino Unido, donde el propio monarca es la cabeza de la Iglesia. Otros países son constitucionalmente laicos o seculares, separando religión y Estado de forma explícita, como Francia, la India o Estados Unidos. Esta distinción no es solo simbólica: influye en leyes de familia, códigos de vestimenta, calendarios oficiales y, como se ve en varios de los conflictos geopolíticos actuales, en las tensiones entre países y dentro de ellos.",
          ],
        },
      ],
    },
    en: {
      title: "Religions of the world",
      summary:
        "Which religions are most practiced, how they're distributed, and why they matter to geopolitics.",
      sections: [
        {
          heading: "The most practiced",
          paragraphs: [
            "Based on global demographic estimates, Christianity is the religion with the most adherents (around 2.4 billion), followed by Islam (around 1.9 billion). Next come Hinduism (around 1.2 billion, mostly in India), Buddhism (around 500 million, concentrated in East and Southeast Asia), and folk or traditional religions, practiced by hundreds of millions of people mainly in Africa and Asia. Judaism has around 15 million adherents worldwide, a small figure relative to its enormous historical and cultural weight. In addition, more than 1 billion people are estimated to identify with no religion at all.",
          ],
        },
        {
          heading: "How they're distributed geographically",
          paragraphs: [
            "A country's dominant religion usually reflects its history: Christianity spread across the Americas, Sub-Saharan Africa and Oceania largely through European colonization; Islam spread across North Africa, the Middle East and South and Southeast Asia through trade and successive conquests from the 7th century onward; Hinduism and Buddhism originated in the Indian subcontinent, from where Buddhism spread to East Asia via trade routes and missionaries.",
          ],
        },
        {
          heading: "Religion and the state",
          paragraphs: [
            "Many countries have an official or state religion: Islam in Saudi Arabia and Iran, Buddhism in Thailand and Bhutan, or Anglican Christianity in the United Kingdom, where the monarch is themself the head of the Church. Other countries are constitutionally secular, explicitly separating religion and state, like France, India or the United States. That distinction isn't just symbolic: it shapes family law, dress codes, official calendars and, as seen in several of today's geopolitical conflicts, tensions both between and within countries.",
          ],
        },
      ],
    },
  },
  {
    slug: "idiomas-del-mundo",
    slugEn: "world-languages",
    emoji: "🗣️",
    category: "society",
    related: ["religiones-del-mundo", "colonizacion-divisiones", "commonwealth"],
    es: {
      title: "Idiomas del mundo",
      summary:
        "Cuántos idiomas se hablan, por qué unos pocos dominan la comunicación global, y qué queda de las lenguas coloniales.",
      sections: [
        {
          heading: "Cuántos idiomas existen",
          paragraphs: [
            "Se estiman entre 6.000 y 7.000 idiomas vivos en el mundo, pero la distribución es muy desigual: apenas 23 lenguas concentran más de la mitad de la población mundial como lengua materna. Se calcula, además, que hasta el 40% de los idiomas actuales corre riesgo de desaparecer en las próximas décadas, muchos de ellos con menos de 1.000 hablantes.",
          ],
        },
        {
          heading: "Las lenguas más habladas",
          paragraphs: [
            "Por cantidad total de hablantes (nativos y no nativos), el inglés y el mandarín encabezan la lista, seguidos por el hindi, el español, el francés y el árabe. El inglés en particular funciona como lingua franca en la ciencia, la aviación, los negocios internacionales e internet, aunque es lengua materna de apenas una minoría de quienes lo usan a diario.",
          ],
        },
        {
          heading: "El legado colonial en los idiomas oficiales",
          paragraphs: [
            "Buena parte de los idiomas oficiales del mundo actual —el inglés en India o Nigeria, el francés en gran parte de África occidental, el portugués en Brasil y Mozambique, el español en América Latina— no son originarios de esos territorios, sino una herencia directa de la colonización europea. En muchos países conviven como idioma oficial junto a decenas o cientos de lenguas indígenas.",
          ],
        },
        {
          heading: "Idiomas oficiales vs. idiomas nacionales",
          paragraphs: [
            "No es lo mismo el idioma que usa el Estado para gobernar (el oficial) que el que habla la mayoría de la población, que a veces es distinto: India reconoce 22 idiomas oficiales a nivel regional, mientras que Papúa Nueva Guinea, con apenas unos 10 millones de habitantes, tiene más de 800 lenguas habladas dentro de sus fronteras: la mayor diversidad lingüística del planeta.",
          ],
        },
      ],
    },
    en: {
      title: "Languages of the world",
      summary:
        "How many languages are spoken, why a handful dominate global communication, and what's left of colonial-era languages.",
      sections: [
        {
          heading: "How many languages exist",
          paragraphs: [
            "There are an estimated 6,000 to 7,000 living languages in the world, but the distribution is deeply uneven: just 23 languages account for more than half of the world's population as a native tongue. It's also estimated that up to 40% of today's languages are at risk of disappearing in the coming decades, many of them with fewer than 1,000 speakers.",
          ],
        },
        {
          heading: "The most widely spoken languages",
          paragraphs: [
            "By total number of speakers (native and non-native), English and Mandarin top the list, followed by Hindi, Spanish, French and Arabic. English in particular functions as a lingua franca in science, aviation, international business and the internet, even though it's the native language of only a minority of those who use it daily.",
          ],
        },
        {
          heading: "The colonial legacy in official languages",
          paragraphs: [
            "Many of today's official languages — English in India or Nigeria, French across much of West Africa, Portuguese in Brazil and Mozambique, Spanish in Latin America — aren't native to those territories at all, but a direct legacy of European colonization. In many countries they coexist as the official language alongside dozens or hundreds of indigenous languages.",
          ],
        },
        {
          heading: "Official languages vs. national languages",
          paragraphs: [
            "The language a state uses to govern (the official one) isn't always the one most of the population actually speaks: India recognizes 22 official languages at the regional level, while Papua New Guinea, with barely 10 million people, has more than 800 languages spoken within its borders — the greatest linguistic diversity on the planet.",
          ],
        },
      ],
    },
  },
  {
    slug: "migracion-diasporas",
    slugEn: "migration-and-diasporas",
    emoji: "🧳",
    category: "society",
    related: ["esclavitud-comercio-triangular", "eeuu-medio-oriente", "colonizacion-divisiones"],
    es: {
      title: "Migración y diásporas",
      summary:
        "Por qué millones de personas viven fuera del país donde nacieron, y cómo esos movimientos moldearon el mapa humano del mundo.",
      sections: [
        {
          heading: "Una cifra en constante crecimiento",
          paragraphs: [
            "Se estima que más de 280 millones de personas viven hoy fuera de su país de nacimiento, cerca del 3,6% de la población mundial. La cifra incluye tanto a quienes migran de forma voluntaria en busca de trabajo o mejores condiciones de vida, como a quienes son desplazados por la fuerza.",
          ],
        },
        {
          heading: "Refugiados y desplazados",
          paragraphs: [
            "A diferencia de un migrante económico, un refugiado es alguien que huye de su país por persecución, guerra o violencia, y tiene un estatus protegido por el derecho internacional desde la Convención de Ginebra de 1951. Conflictos como los que atraviesan Siria, Afganistán, Venezuela o Sudán generaron algunas de las mayores crisis de desplazamiento de las últimas décadas.",
          ],
        },
        {
          heading: "Diásporas que moldearon países enteros",
          paragraphs: [
            "Algunas migraciones masivas del pasado explican la composición demográfica de países enteros hoy: el comercio triangular de personas esclavizadas, la diáspora india hacia el Caribe y África oriental durante el Imperio Británico, o la emigración europea masiva hacia América entre fines del siglo XIX y principios del XX, que transformó la demografía de países como Argentina, Brasil y Estados Unidos.",
          ],
        },
        {
          heading: "Remesas: el otro lado de la migración",
          paragraphs: [
            "El dinero que las personas migrantes envían a sus países de origen —las remesas— representa una fuente de ingresos clave para muchas economías en desarrollo, en algunos casos equivalente a una porción mayor del PBI nacional que la inversión extranjera y la ayuda internacional combinadas.",
          ],
        },
      ],
    },
    en: {
      title: "Migration and diasporas",
      summary:
        "Why hundreds of millions of people live outside the country where they were born, and how those movements shaped the human map of the world.",
      sections: [
        {
          heading: "A steadily growing figure",
          paragraphs: [
            "An estimated 280 million people now live outside their country of birth, close to 3.6% of the world's population. That figure includes both people who migrate voluntarily in search of work or better living conditions, and those who are forcibly displaced.",
          ],
        },
        {
          heading: "Refugees and the displaced",
          paragraphs: [
            "Unlike an economic migrant, a refugee is someone fleeing their country due to persecution, war or violence, and holds a status protected under international law since the 1951 Geneva Convention. Conflicts in Syria, Afghanistan, Venezuela and Sudan have produced some of the largest displacement crises of recent decades.",
          ],
        },
        {
          heading: "Diasporas that shaped entire countries",
          paragraphs: [
            "Some mass migrations of the past explain the demographic makeup of entire countries today: the triangular trade in enslaved people, the Indian diaspora across the Caribbean and East Africa during the British Empire, or the massive European emigration to the Americas between the late 19th and early 20th centuries, which transformed the demographics of countries like Argentina, Brazil and the United States.",
          ],
        },
        {
          heading: "Remittances: migration's other side",
          paragraphs: [
            "The money migrants send back to their home countries — remittances — is a key source of income for many developing economies, in some cases outweighing foreign investment and international aid combined as a share of national GDP.",
          ],
        },
      ],
    },
  },
  {
    slug: "deporte-identidad-nacional",
    slugEn: "sport-and-national-identity",
    emoji: "🏆",
    category: "society",
    related: [
      "commonwealth",
      "guerra-fria",
      "estado-nacion-westfalia",
      "arte-literatura-musica",
    ],
    es: {
      title: "Deporte e identidad nacional",
      summary:
        "Cómo el deporte se convirtió en una de las formas más visibles —y más pacíficas— de competencia entre países.",
      sections: [
        {
          heading: "El deporte como diplomacia (y como poder blando)",
          paragraphs: [
            "Eventos como el Mundial de fútbol o los Juegos Olímpicos reúnen a la mayoría de los países del mundo compitiendo bajo su propia bandera, en lo que suele describirse como una forma de \"poder blando\": construir prestigio e influencia internacional sin recurrir a la fuerza. Episodios como la \"diplomacia del ping pong\" entre Estados Unidos y China en 1971 muestran cómo el deporte también abrió puertas políticas que la diplomacia tradicional tenía cerradas.",
          ],
        },
        {
          heading: "Cuando el deporte refleja la geopolítica",
          paragraphs: [
            "El boicot de decenas de países a los Juegos Olímpicos de Moscú 1980 (y la represalia soviética en Los Ángeles 1984) durante la Guerra Fría, o la exclusión de Sudáfrica de las competencias internacionales durante el apartheid, muestran que el deporte rara vez queda al margen de los conflictos y divisiones políticas del resto del mundo.",
          ],
        },
        {
          heading: "Competencias entre países que no son países",
          paragraphs: [
            "No todos los eventos deportivos siguen exactamente el mapa político: los Juegos de la Commonwealth reúnen solo a los países de esa asociación, mientras que Escocia, Gales o Irlanda del Norte compiten como selecciones propias en fútbol pese a formar parte del Reino Unido, y Puerto Rico participa con delegación propia en los Juegos Olímpicos aunque sea un territorio de Estados Unidos.",
          ],
        },
        {
          heading: "Una identidad compartida",
          paragraphs: [
            "Más allá de la competencia, estos eventos suelen ser uno de los pocos momentos en que la mayoría de la población de un país comparte una misma identidad colectiva al mismo tiempo, algo que muchos historiadores vinculan directamente con la construcción del propio concepto de nación.",
          ],
        },
      ],
    },
    en: {
      title: "Sport and national identity",
      summary:
        "How sport became one of the most visible — and most peaceful — forms of competition between countries.",
      sections: [
        {
          heading: "Sport as diplomacy (and as soft power)",
          paragraphs: [
            "Events like the football World Cup or the Olympic Games bring together most of the world's countries competing under their own flag, in what's often described as a form of \"soft power\": building international prestige and influence without resorting to force. Episodes like the US-China \"ping-pong diplomacy\" of 1971 show how sport has also opened political doors that traditional diplomacy kept closed.",
          ],
        },
        {
          heading: "When sport mirrors geopolitics",
          paragraphs: [
            "The boycott by dozens of countries of the 1980 Moscow Olympics (and the Soviet retaliation at the 1984 Los Angeles Games) during the Cold War, or South Africa's exclusion from international competition during apartheid, show that sport is rarely insulated from the world's political conflicts and divisions.",
          ],
        },
        {
          heading: "Competitions between countries that aren't countries",
          paragraphs: [
            "Not every sporting event follows the political map exactly: the Commonwealth Games bring together only the members of that association, while Scotland, Wales and Northern Ireland compete as their own national football teams despite being part of the United Kingdom, and Puerto Rico fields its own delegation at the Olympics despite being a US territory.",
          ],
        },
        {
          heading: "A shared identity",
          paragraphs: [
            "Beyond the competition itself, these events tend to be one of the few moments when most of a country's population shares a single collective identity at the same time — something many historians link directly to the construction of the very concept of a nation.",
          ],
        },
      ],
    },
  },
  {
    slug: "capitalismo-y-alternativas",
    slugEn: "capitalism-and-alternatives",
    emoji: "💱",
    category: "development",
    related: ["guerra-fria", "revolucion-industrial", "union-europea"],
    es: {
      title: "Capitalismo: cómo funciona y qué alternativas existen",
      summary:
        "Los principios básicos del sistema económico dominante, sus variantes, y qué otros modelos existieron o existen.",
      sections: [
        {
          heading: "Cómo funciona el capitalismo",
          paragraphs: [
            "Se basa en la propiedad privada de los medios de producción (fábricas, tierras, empresas), en mercados donde la oferta y la demanda determinan los precios, y en la búsqueda de ganancia como principal incentivo para producir e innovar. La competencia entre empresas es, en teoría, lo que empuja los precios hacia abajo y la calidad hacia arriba. El rol del Estado varía mucho de un país a otro: desde una intervención mínima hasta una fuerte regulación y provisión de servicios públicos.",
          ],
        },
        {
          heading: "No hay un solo capitalismo",
          paragraphs: [
            "El capitalismo liberal o de libre mercado (más asociado a Estados Unidos) minimiza la intervención estatal en la economía. La economía social de mercado o \"modelo nórdico\" (Suecia, Noruega, Dinamarca) combina mercados libres con una fuerte carga impositiva y un Estado de bienestar universal en salud, educación y jubilaciones. El capitalismo de Estado (China es el ejemplo más citado) combina mercados y empresas privadas con una fuerte participación y control estatal en sectores considerados estratégicos.",
          ],
        },
        {
          heading: "Alternativas históricas: socialismo y comunismo",
          paragraphs: [
            "El socialismo propone la propiedad colectiva o estatal de los medios de producción para lograr una distribución más igualitaria de la riqueza; existió en su forma más radical en la URSS y sigue orientando políticas de bienestar en muchas democracias actuales, generalmente combinado con mercados (ver el artículo sobre la Guerra Fría). El comunismo, en la teoría marxista, es la etapa final sin clases sociales ni Estado; en la práctica, los países que se autodenominaron comunistas en el siglo XX desarrollaron economías centralizadas y planificadas por el Estado, con resultados muy distintos según el caso.",
          ],
        },
        {
          heading: "Alternativas dentro del capitalismo",
          paragraphs: [
            "El cooperativismo propone empresas de propiedad y gestión colectiva de sus propios trabajadores o miembros; conviven dentro de economías capitalistas como complemento, desde cooperativas agrícolas hasta bancos cooperativos y de crédito. Corrientes más recientes, como el decrecimiento o la economía del bien común, cuestionan el crecimiento económico infinito como objetivo en sí mismo y proponen priorizar la sostenibilidad ambiental y el bienestar por sobre el aumento constante de la producción.",
          ],
        },
      ],
    },
    en: {
      title: "Capitalism: how it works and what alternatives exist",
      summary:
        "The basic principles of the dominant economic system, its variants, and what other models existed or exist today.",
      sections: [
        {
          heading: "How capitalism works",
          paragraphs: [
            "It rests on private ownership of the means of production (factories, land, businesses), on markets where supply and demand set prices, and on profit as the main incentive to produce and innovate. Competition between businesses is, in theory, what pushes prices down and quality up. The state's role varies widely from country to country, from minimal intervention to heavy regulation and public service provision.",
          ],
        },
        {
          heading: "There's no single capitalism",
          paragraphs: [
            "Liberal or free-market capitalism (most associated with the United States) keeps state intervention in the economy to a minimum. The social market economy, or \"Nordic model\" (Sweden, Norway, Denmark), combines free markets with heavy taxation and a universal welfare state covering health, education and pensions. State capitalism (China is the most cited example) combines markets and private businesses with heavy state participation and control in sectors considered strategic.",
          ],
        },
        {
          heading: "Historical alternatives: socialism and communism",
          paragraphs: [
            "Socialism proposes collective or state ownership of the means of production to achieve a more equal distribution of wealth; it existed in its most radical form in the USSR and still shapes welfare policy in many democracies today, usually combined with markets (see the Cold War article). Communism, in Marxist theory, is the final, classless, stateless stage; in practice, the countries that called themselves communist in the 20th century built centralized, state-planned economies, with very different results case by case.",
          ],
        },
        {
          heading: "Alternatives within capitalism",
          paragraphs: [
            "Cooperativism proposes businesses owned and run collectively by their own workers or members; these coexist within capitalist economies as a complement, from agricultural cooperatives to cooperative and credit banks. More recent currents, like degrowth or the economy for the common good, question infinite economic growth as a goal in itself, proposing to prioritize environmental sustainability and well-being over constantly increasing output.",
          ],
        },
      ],
    },
  },
  {
    slug: "eeuu-medio-oriente",
    slugEn: "us-and-the-middle-east",
    emoji: "🛢️",
    category: "history",
    related: ["guerra-fria", "religiones-del-mundo", "migracion-diasporas"],
    es: {
      title: "Estados Unidos y Medio Oriente: conflictos y tensiones",
      summary:
        "Un vínculo marcado por petróleo, alianzas militares y décadas de conflictos armados, todavía sin resolverse.",
      sections: [
        {
          heading: "Por qué Medio Oriente es estratégico para EE.UU.",
          paragraphs: [
            "La región concentra buena parte de las reservas mundiales de petróleo, rutas comerciales clave y aliados militares de larga data (Israel, Arabia Saudita y varios países del Golfo). Desde los atentados del 11 de septiembre de 2001, se sumó otro factor: la llamada \"guerra contra el terrorismo\", que llevó a intervenciones militares directas en la región durante las dos décadas siguientes.",
          ],
        },
        {
          heading: "Principales intervenciones militares de EE.UU.",
          list: [
            "Guerra del Golfo (1990-1991): una coalición liderada por EE.UU. expulsó a las tropas iraquíes de Kuwait, que Irak había invadido en 1990.",
            "Guerra de Afganistán (2001-2021): comenzó tras los atentados del 11-S, con el objetivo declarado de desmantelar a Al Qaeda y derrocar al régimen talibán que le daba refugio. Se convirtió en el conflicto armado más largo de la historia de EE.UU. y terminó con el retorno de los talibanes al poder.",
            "Guerra de Irak (2003-2011): se basó en la acusación —luego refutada— de que el régimen de Saddam Hussein poseía armas de destrucción masiva. Derrocó a Hussein pero generó un vacío de poder que derivó en años de violencia sectaria y facilitó el surgimiento del grupo ISIS.",
            "Intervención en Siria (desde 2014): tropas y fuerzas aéreas de EE.UU. operan principalmente contra el ISIS en el marco de la guerra civil siria, iniciada en 2011.",
          ],
        },
        {
          heading: "El conflicto entre Israel y Palestina",
          paragraphs: [
            "Tiene sus raíces en el reparto del Mandato Británico de Palestina tras la Segunda Guerra Mundial y la creación del Estado de Israel en 1948, seguida de sucesivas guerras árabe-israelíes (1948, 1967, 1973). Desde la Guerra de los Seis Días en 1967, Israel ocupa Cisjordania y, hasta 2005, también controló militarmente la Franja de Gaza. Estados Unidos es históricamente el principal aliado y proveedor de ayuda militar de Israel, mientras gran parte del mundo árabe y musulmán respalda la causa de un Estado palestino independiente. El conflicto atraviesa períodos de escalada recurrentes, como la guerra en Gaza iniciada en octubre de 2023 tras un ataque de Hamás contra Israel, y sigue sin una resolución definitiva.",
          ],
        },
        {
          heading: "Tensiones con Irán",
          paragraphs: [
            "EE.UU. e Irán rompieron relaciones diplomáticas tras la Revolución Islámica de 1979 y la posterior crisis de los rehenes en la embajada estadounidense en Teherán. Desde entonces, la relación estuvo marcada por sanciones económicas —sobre todo por el programa nuclear iraní—, un acuerdo nuclear firmado en 2015 (conocido como JCPOA) que limitaba ese programa a cambio de aliviar sanciones, y el retiro unilateral de EE.UU. de ese acuerdo en 2018. Ambos países también se enfrentan de forma indirecta a través de aliados y milicias en Líbano, Siria, Yemen e Irak.",
          ],
        },
        {
          heading: "Una región en constante cambio",
          paragraphs: [
            "Estos conflictos evolucionan permanentemente y los hechos suelen interpretarse de forma distinta según la fuente. Para entender su estado actual conviene complementar esta introducción histórica con múltiples fuentes periodísticas actualizadas.",
          ],
        },
      ],
    },
    en: {
      title: "The United States and the Middle East: conflicts and tensions",
      summary:
        "A relationship shaped by oil, military alliances and decades of armed conflict, still unresolved.",
      sections: [
        {
          heading: "Why the Middle East is strategic for the US",
          paragraphs: [
            "The region holds a large share of the world's oil reserves, key trade routes, and long-standing military allies (Israel, Saudi Arabia and several Gulf countries). Since the September 11, 2001 attacks, another factor was added: the so-called \"War on Terror\", which led to direct US military interventions in the region over the following two decades.",
          ],
        },
        {
          heading: "Major US military interventions",
          list: [
            "Gulf War (1990-1991): a US-led coalition drove Iraqi troops out of Kuwait, which Iraq had invaded in 1990.",
            "Afghanistan War (2001-2021): began after the 9/11 attacks, with the stated goal of dismantling Al-Qaeda and toppling the Taliban regime that sheltered it. It became the longest armed conflict in US history and ended with the Taliban returning to power.",
            "Iraq War (2003-2011): based on the claim — later disproven — that Saddam Hussein's regime possessed weapons of mass destruction. It toppled Hussein but created a power vacuum that led to years of sectarian violence and helped enable the rise of ISIS.",
            "Intervention in Syria (since 2014): US troops and air power operate mainly against ISIS within the broader Syrian civil war, which began in 2011.",
          ],
        },
        {
          heading: "The Israeli-Palestinian conflict",
          paragraphs: [
            "It's rooted in the partition of the British Mandate for Palestine after World War II and the creation of the State of Israel in 1948, followed by successive Arab-Israeli wars (1948, 1967, 1973). Since the Six-Day War in 1967, Israel has occupied the West Bank, and until 2005 it also militarily controlled the Gaza Strip. The United States is historically Israel's main ally and provider of military aid, while much of the Arab and Muslim world backs the cause of an independent Palestinian state. The conflict goes through recurring periods of escalation, such as the war in Gaza that began in October 2023 after a Hamas attack on Israel, and remains without a definitive resolution.",
          ],
        },
        {
          heading: "Tensions with Iran",
          paragraphs: [
            "The US and Iran broke off diplomatic relations after the 1979 Islamic Revolution and the subsequent hostage crisis at the US embassy in Tehran. Since then, the relationship has been marked by economic sanctions — mainly over Iran's nuclear program — a 2015 nuclear agreement (known as the JCPOA) that limited that program in exchange for sanctions relief, and the US's unilateral withdrawal from that agreement in 2018. Both countries also face off indirectly through allies and militias in Lebanon, Syria, Yemen and Iraq.",
          ],
        },
        {
          heading: "A region in constant flux",
          paragraphs: [
            "These conflicts keep evolving, and the facts are often interpreted differently depending on the source. To understand their current state, it's worth pairing this historical primer with multiple up-to-date news sources.",
          ],
        },
      ],
    },
  },
  {
    slug: "malvinas-falklands",
    slugEn: "malvinas-falklands",
    emoji: "🏝️",
    category: "geopolitics",
    related: ["colonizacion-divisiones", "estado-nacion-westfalia", "onu"],
    es: {
      title: "Islas Malvinas: el reclamo argentino y la disputa de soberanía",
      summary:
        "Por qué Argentina reclama las islas, qué dice el Reino Unido, y cómo sigue sin resolverse la disputa territorial más antigua de Sudamérica.",
      sections: [
        {
          heading: "El reclamo argentino",
          paragraphs: [
            "Argentina reclama la soberanía sobre las Islas Malvinas —y también sobre las Islas Georgias del Sur y Sandwich del Sur— basándose en varios argumentos: la herencia de los derechos españoles sobre el archipiélago tras la independencia en 1816 (España había reclamado las islas desde el siglo XVIII); el ejercicio efectivo de la administración argentina entre 1820 y 1833, con autoridades designadas y asentamientos propios; y la cercanía geográfica, ya que las islas están a poco más de 400 km de la costa de la Patagonia y a más de 12.000 km del Reino Unido.",
            "Este reclamo está incorporado a la Constitución Nacional argentina, cuya disposición transitoria primera declara que la recuperación de las Malvinas y el ejercicio pleno de la soberanía sobre ellas, respetando el modo de vida de sus habitantes, es un objetivo permanente e irrenunciable del pueblo argentino, a alcanzar por vías pacíficas y de acuerdo con el derecho internacional.",
            "Desde 1965, la Asamblea General de la ONU aprobó varias resoluciones (la más citada, la 2065) que reconocen la existencia de una disputa de soberanía entre Argentina y el Reino Unido, e invitan a ambos países a negociarla.",
          ],
        },
        {
          heading: "1833: el punto de quiebre",
          paragraphs: [
            "En enero de 1833, una fragata británica desembarcó en las islas y expulsó a las autoridades y pobladores argentinos que las administraban desde hacía más de una década, reafirmando un reclamo británico previo de 1770. Argentina protestó formalmente ante el Reino Unido ese mismo año y nunca reconoció ese acto como legítimo, considerándolo el origen de la disputa que continúa hoy.",
          ],
        },
        {
          heading: "La posición del Reino Unido y de los isleños",
          paragraphs: [
            "El Reino Unido sostiene su soberanía sobre las islas de forma ininterrumpida desde 1833 (con la excepción de la ocupación argentina de 1982) y basa su postura, sobre todo, en el principio de autodeterminación de los pueblos consagrado por la ONU. Los cerca de 3.500 habitantes de las islas, en su gran mayoría de origen británico, se manifestaron en un referéndum de 2013 con un 99,8% de los votos a favor de seguir siendo un Territorio Británico de Ultramar autogobernado.",
            "Argentina responde que el principio de autodeterminación no aplica en este caso, porque sostiene que la población actual de las islas desciende de colonos instalados por el Reino Unido después de la expulsión de 1833, y no de un pueblo originario desplazado.",
          ],
        },
        {
          heading: "La guerra de 1982",
          paragraphs: [
            "El 2 de abril de 1982, la última dictadura militar argentina desembarcó tropas y ocupó las islas para intentar imponer el reclamo por la fuerza. El Reino Unido respondió enviando una fuerza naval que recuperó el control el 14 de junio del mismo año, tras 74 días de guerra. Murieron 649 militares argentinos, 255 militares británicos y 3 isleños.",
            "La derrota militar aceleró la caída de la dictadura y el retorno de la democracia en Argentina en 1983, además de reforzar en el Reino Unido la postura de no negociar la soberanía.",
          ],
        },
        {
          heading: "El estado actual de la disputa",
          paragraphs: [
            "Argentina sigue reclamando la soberanía por la vía diplomática, a través de organismos como la ONU, la OEA y la CELAC, sin renunciar a esa reivindicación. El Reino Unido, por su parte, considera la cuestión resuelta y administra las islas desde 2009 bajo su propia constitución, como Territorio Británico de Ultramar autogobernado. El Comité de Descolonización de la ONU sigue clasificando a las islas como un \"territorio no autónomo\" pendiente de descolonización, e insta a ambas partes a retomar el diálogo bilateral, algo que en la práctica no ocurre desde hace años.",
          ],
        },
      ],
    },
    en: {
      title: "The Falkland/Malvinas Islands: Argentina's claim and the sovereignty dispute",
      summary:
        "Why Argentina claims the islands, what the UK says, and how South America's oldest territorial dispute remains unresolved.",
      sections: [
        {
          heading: "Argentina's claim",
          paragraphs: [
            "Argentina claims sovereignty over the Falkland/Malvinas Islands — and also over South Georgia and the South Sandwich Islands — based on several arguments: inheriting Spain's rights over the archipelago after independence in 1816 (Spain had claimed the islands since the 18th century); Argentina's effective administration of the islands between 1820 and 1833, with appointed authorities and its own settlements; and geographic proximity, since the islands sit just over 400 km from the Patagonian coast and more than 12,000 km from the United Kingdom.",
            "This claim is written into Argentina's National Constitution, whose first transitional provision declares that recovering the Malvinas and fully exercising sovereignty over them, respecting the islanders' way of life, is a permanent, non-negotiable goal of the Argentine people, to be achieved by peaceful means and in accordance with international law.",
            "Since 1965, the UN General Assembly has passed several resolutions (most notably Resolution 2065) recognizing the existence of a sovereignty dispute between Argentina and the United Kingdom, and inviting both countries to negotiate it.",
          ],
        },
        {
          heading: "1833: the turning point",
          paragraphs: [
            "In January 1833, a British frigate landed on the islands and expelled the Argentine authorities and settlers who had administered them for over a decade, reasserting an earlier British claim from 1770. Argentina formally protested to the United Kingdom that same year and never recognized the act as legitimate, considering it the origin of the dispute that continues today.",
          ],
        },
        {
          heading: "The UK's and the islanders' position",
          paragraphs: [
            "The United Kingdom has maintained sovereignty over the islands uninterrupted since 1833 (except for the 1982 Argentine occupation) and bases its position chiefly on the principle of self-determination enshrined by the UN. The roughly 3,500 islanders, overwhelmingly of British descent, voted in a 2013 referendum with 99.8% in favor of remaining a self-governing British Overseas Territory.",
            "Argentina counters that self-determination doesn't apply in this case, arguing that today's islanders descend from settlers the UK brought in after the 1833 expulsion, rather than from a displaced indigenous population.",
          ],
        },
        {
          heading: "The 1982 war",
          paragraphs: [
            "On April 2, 1982, Argentina's last military dictatorship landed troops and occupied the islands in an attempt to force the claim through military means. The United Kingdom responded by sending a naval task force that retook control on June 14 of that year, after 74 days of war. 649 Argentine service members, 255 British service members and 3 islanders died.",
            "The military defeat hastened the dictatorship's collapse and Argentina's return to democracy in 1983, while hardening the UK's position against negotiating sovereignty.",
          ],
        },
        {
          heading: "Where things stand today",
          paragraphs: [
            "Argentina continues to press its sovereignty claim diplomatically, through bodies like the UN, the OAS and CELAC, without ever renouncing it. The United Kingdom, for its part, considers the matter settled and has administered the islands since 2009 under their own constitution, as a self-governing British Overseas Territory. The UN's Decolonization Committee still lists the islands as a \"non-self-governing territory\" pending decolonization, and urges both sides to resume bilateral talks — something that, in practice, hasn't happened in years.",
          ],
        },
      ],
    },
  },
  {
    slug: "mar-zonas-economicas",
    slugEn: "the-sea-each-country-gets",
    emoji: "🌊",
    category: "geopolitics",
    related: ["malvinas-falklands", "cuantos-paises-hay", "cambio-climatico"],
    es: {
      title: "El mar que le toca a cada país",
      summary:
        "Cada país de esta app muestra su tamaño en kilómetros cuadrados de tierra. Varios son mucho más grandes de lo que ese número dice.",
      sections: [
        {
          heading: "Doce millas, y después doscientas",
          paragraphs: [
            "La Convención de las Naciones Unidas sobre el Derecho del Mar, firmada en 1982 y en vigor desde 1994, ordenó algo que durante siglos se había resuelto a los tiros. Un país ejerce soberanía plena sobre su mar territorial, las primeras doce millas náuticas desde la costa. Más allá, hasta las doscientas millas, tiene una zona económica exclusiva.",
            "La diferencia entre las dos importa y casi siempre se pasa por alto: la zona económica exclusiva no es territorio. Cualquier barco puede navegarla y cualquier avión sobrevolarla. Lo que es exclusivo son los recursos — los peces, el petróleo, el gas, los minerales del fondo marino. Es un derecho sobre lo que hay, no sobre el espacio.",
          ],
        },
        {
          heading: "Los países que son enormes por mar",
          paragraphs: [
            "Francia tiene la zona económica exclusiva más grande del planeta, alrededor de diez millones de kilómetros cuadrados, y casi nada de eso está cerca de Francia: sale de la Polinesia, Nueva Caledonia, las islas del Índico y la Guayana. Su superficie marítima es unas veinte veces su territorio europeo.",
            "El contraste más extremo es Kiribati. Su tierra suma unos 800 kilómetros cuadrados —menos que muchas ciudades— repartidos en atolones diminutos, pero esos atolones están tan separados entre sí que su zona económica exclusiva supera los tres millones de kilómetros cuadrados. En una lista ordenada por tierra, Kiribati es de los últimos. En una ordenada por mar, está entre los primeros.",
            "Esto reordena por completo cómo se leen los estados insulares del Pacífico y del Caribe que aparecen en esta app. No son países chicos con poco que negociar: son países con jurisdicción sobre enormes extensiones de océano, y eso es exactamente lo que negocian.",
          ],
        },
        {
          heading: "Por qué una roca puede valer un país",
          paragraphs: [
            "Si una isla genera doscientas millas de derechos a su alrededor, entonces la pregunta de qué cuenta como isla deja de ser geográfica y pasa a ser política. La convención lo previó: las rocas que no pueden sostener habitación humana ni vida económica propia generan mar territorial, pero no zona económica exclusiva.",
            "De ahí que varios países hayan construido sobre arrecifes, plantado vegetación en islotes o instalado guarniciones permanentes en lugares donde nadie viviría por gusto. En 2016, un tribunal arbitral resolvió que varios accidentes del archipiélago de las Spratly, en el Mar de China Meridional, eran rocas y no islas, y por lo tanto no generaban las zonas que se reclamaban sobre ellos.",
          ],
        },
        {
          heading: "Un problema nuevo: la costa que se mueve",
          paragraphs: [
            "Las zonas marítimas se miden desde la línea de base costera. Si el mar sube y la costa retrocede, la línea se corre y la zona económica se encoge — justo para los países que menos contribuyeron al calentamiento y más dependen de su mar. Por eso los estados del Pacífico impulsaron declaraciones para fijar sus límites marítimos de forma permanente, con independencia de dónde termine estando la orilla.",
            "Y explica algo que esta app ya menciona en otro tema: buena parte de la disputa por las Malvinas no es por las islas sino por lo que rodea a las islas. Pesca, y potencialmente hidrocarburos, en un área de mar mucho mayor que la superficie en disputa.",
          ],
        },
      ],
    },
    en: {
      title: "The sea each country gets",
      summary:
        "Every country in this app shows its size in square kilometres of land. Several are far larger than that number says.",
      sections: [
        {
          heading: "Twelve miles, and then two hundred",
          paragraphs: [
            "The UN Convention on the Law of the Sea, signed in 1982 and in force since 1994, settled by rule something that had been settled by gunfire for centuries. A country holds full sovereignty over its territorial sea, the first twelve nautical miles from the coast. Beyond that, out to two hundred miles, it has an exclusive economic zone.",
            "The difference between the two matters and is almost always missed: the exclusive economic zone is not territory. Any ship may sail it and any aircraft fly over it. What is exclusive are the resources — the fish, the oil, the gas, the minerals on the seabed. It is a right over what is there, not over the space.",
          ],
        },
        {
          heading: "The countries that are vast by sea",
          paragraphs: [
            "France has the largest exclusive economic zone on earth, around ten million square kilometres, and almost none of it is near France: it comes from Polynesia, New Caledonia, the Indian Ocean islands and Guiana. Its maritime surface is some twenty times its European territory.",
            "The starkest contrast is Kiribati. Its land adds up to about 800 square kilometres — less than many cities — spread across tiny atolls, but those atolls sit so far apart that its exclusive economic zone passes three million square kilometres. On a list ordered by land, Kiribati is near the bottom. On one ordered by sea, it is near the top.",
            "This reorders how the Pacific and Caribbean island states in this app should be read. They are not small countries with little to negotiate: they are countries with jurisdiction over enormous stretches of ocean, and that is precisely what they negotiate.",
          ],
        },
        {
          heading: "Why a rock can be worth a country",
          paragraphs: [
            "If an island generates two hundred miles of rights around it, then the question of what counts as an island stops being geographical and becomes political. The convention anticipated it: rocks which cannot sustain human habitation or an economic life of their own generate a territorial sea, but no exclusive economic zone.",
            "Hence several countries building on reefs, planting vegetation on islets, or stationing permanent garrisons in places nobody would live by choice. In 2016 an arbitral tribunal found that several features in the Spratly archipelago, in the South China Sea, were rocks rather than islands, and therefore generated none of the zones being claimed from them.",
          ],
        },
        {
          heading: "A new problem: the coastline moves",
          paragraphs: [
            "Maritime zones are measured from a coastal baseline. If the sea rises and the coast retreats, the line shifts and the economic zone shrinks — precisely for the countries that contributed least to the warming and depend most on their sea. That is why Pacific states pushed declarations fixing their maritime boundaries permanently, whatever happens to the shoreline.",
            "And it explains something this app raises in another topic: much of the dispute over the Falklands is not about the islands but about what surrounds them. Fishing, and potentially hydrocarbons, across an area of sea far larger than the land in question.",
          ],
        },
      ],
    },
  },
  {
    slug: "ciudades-urbanizacion",
    slugEn: "cities-and-urbanisation",
    emoji: "🏙️",
    category: "society",
    related: ["migracion-diasporas", "revolucion-industrial", "pbi-idh"],
    es: {
      title: "Cuando la mitad del mundo se mudó a la ciudad",
      summary:
        "Alrededor de 2007 la humanidad pasó a ser mayoritariamente urbana por primera vez. Es un dato reciente, y explica más de lo que parece.",
      sections: [
        {
          heading: "Un cambio de dos siglos, no de dos décadas",
          paragraphs: [
            "Hacia 1800, menos de una persona de cada diez vivía en una ciudad. Hoy vive en ciudades algo más de la mitad de la humanidad, y el cruce de ese umbral ocurrió recién alrededor de 2007. Es un cambio enorme y también reciente: la especie pasó casi toda su historia siendo rural.",
            "El motor original fue la industrialización, que concentró trabajo en fábricas y las fábricas en ciudades. Pero el proceso no se detuvo cuando las fábricas se fueron, y ahí empieza la parte interesante.",
          ],
        },
        {
          heading: "El mismo porcentaje, dos historias distintas",
          paragraphs: [
            "En esta app, el indicador de población urbana va desde menos del 15% en Burundi hasta más del 95% en varios países. Argentina está en 92,4%, muy por encima de la mediana mundial de 63,8%, y prácticamente empatada con Bélgica o Países Bajos.",
            "Pero llegar al mismo número no significa haber recorrido el mismo camino. En Europa la urbanización acompañó a la industrialización: la gente se mudó a donde había empleo fabril. En buena parte de América Latina y África fue más rápida y no vino con esos empleos, un fenómeno que se conoce como urbanización sin industrialización. El resultado son ciudades enormes con una porción grande de su población en empleo informal y en barrios sin servicios formales.",
          ],
        },
        {
          heading: "Las megaciudades no son la historia principal",
          paragraphs: [
            "Tokio, Delhi y Shanghái concentran la atención, y Tokio ronda los 37 millones de habitantes en su área metropolitana. Pero la mayor parte del crecimiento urbano del mundo no ocurre ahí: ocurre en ciudades de menos de un millón de habitantes, que son también las que menos capacidad de planificación tienen.",
            "África es el caso extremo. Es la región menos urbanizada del planeta y la que más rápido se urbaniza, lo que significa que buena parte de las ciudades en las que va a vivir la gente dentro de treinta años todavía se está construyendo — o todavía no empezó a construirse.",
          ],
        },
        {
          heading: "Por qué cambia cómo se lee el mapa",
          paragraphs: [
            "El dato de población de un país no dice si esa gente está repartida o amontonada, y esa diferencia gobierna casi todo lo demás: cuánto cuesta llevar agua y electricidad, cuántas emisiones genera cada persona, qué tan lejos queda un hospital, y hasta cómo se vota — la brecha entre ciudad y campo es hoy una de las divisiones políticas más consistentes del mundo.",
            "Es por eso que la población urbana está entre los indicadores de cada país en esta app, con la mediana mundial al lado. Un 92% no significa nada solo; significa bastante al lado de un 64%.",
          ],
        },
      ],
    },
    en: {
      title: "When half the world moved to the city",
      summary:
        "Around 2007 humanity became majority-urban for the first time. It is a recent fact, and it explains more than it appears to.",
      sections: [
        {
          heading: "A two-century change, not a two-decade one",
          paragraphs: [
            "Around 1800, fewer than one person in ten lived in a city. Today slightly more than half of humanity does, and that threshold was only crossed around 2007. It is an enormous change and also a recent one: the species spent almost all of its history rural.",
            "The original engine was industrialisation, which concentrated work in factories and factories in cities. But the process did not stop when the factories left, and that is where it gets interesting.",
          ],
        },
        {
          heading: "The same percentage, two different stories",
          paragraphs: [
            "In this app the urban-population indicator runs from under 15% in Burundi to over 95% in several countries. Argentina sits at 92.4%, well above the world median of 63.8%, and effectively level with Belgium or the Netherlands.",
            "But arriving at the same number does not mean having travelled the same road. In Europe urbanisation came with industrialisation: people moved to where the factory jobs were. Across much of Latin America and Africa it was faster and did not bring those jobs, a pattern known as urbanisation without industrialisation. The result is enormous cities with a large share of their people in informal work and in neighbourhoods without formal services.",
          ],
        },
        {
          heading: "Megacities are not the main story",
          paragraphs: [
            "Tokyo, Delhi and Shanghai take the attention, and Tokyo's metropolitan area is around 37 million people. But most of the world's urban growth does not happen there: it happens in cities of under a million, which are also the ones with the least capacity to plan.",
            "Africa is the extreme case. It is the least urbanised region on earth and the fastest urbanising, which means much of the city that people will live in thirty years from now is still being built — or has not started being built.",
          ],
        },
        {
          heading: "Why it changes how the map reads",
          paragraphs: [
            "A country's population figure doesn't say whether those people are spread out or packed together, and that difference governs almost everything else: what it costs to deliver water and power, how much each person emits, how far away a hospital is, even how people vote — the urban-rural gap is now one of the most consistent political divides in the world.",
            "That is why urban population sits among each country's indicators in this app, with the world median beside it. 92% means nothing on its own; it means a good deal next to 64%.",
          ],
        },
      ],
    },
  },
  {
    slug: "rios-compartidos",
    slugEn: "shared-rivers",
    emoji: "💧",
    category: "geopolitics",
    related: ["cambio-climatico", "colonizacion-divisiones", "onu"],
    es: {
      title: "Ríos compartidos: quién controla el agua",
      summary:
        "Las fronteras dividen la tierra, pero los ríos las cruzan. Quien está aguas arriba tiene una palanca que quien está aguas abajo no puede igualar.",
      sections: [
        {
          heading: "La asimetría de estar aguas arriba",
          paragraphs: [
            "Más de la mitad del agua dulce que corre por el mundo atraviesa al menos una frontera, y bastante más de cien países comparten alguna cuenca. Eso crea una relación de poder que no tiene equivalente en otros recursos: el petróleo se puede comprar a otro proveedor, pero un río viene de donde viene.",
            "El país de aguas arriba puede represar, desviar o contaminar, y el de aguas abajo no tiene ninguna medida equivalente para responder. Por eso casi todos los conflictos por agua terminan negociándose: no porque los países sean generosos, sino porque el de abajo no tiene otra cosa que hacer más que negociar.",
          ],
        },
        {
          heading: "El Nilo",
          paragraphs: [
            "Egipto es el caso extremo de dependencia: casi toda el agua que usa se origina fuera de sus fronteras, y el país es esencialmente un río rodeado de desierto. Durante décadas esa dependencia estuvo administrada por tratados de la época colonial, firmados en 1929 y 1959, que repartían el caudal entre Egipto y Sudán y no incluían a Etiopía — que es de donde sale la mayor parte del agua.",
            "Etiopía nunca reconoció esos acuerdos, y desde 2011 construyó sobre el Nilo Azul la represa más grande de África, que empezó a llenarse en 2020. Para Etiopía es electricidad para un país donde millones no tienen acceso a ella. Para Egipto es alguien más con la mano en la canilla. Las negociaciones llevan más de una década sin acuerdo definitivo.",
          ],
        },
        {
          heading: "El Indo, y lo que costó que funcionara",
          paragraphs: [
            "El Tratado de las Aguas del Indo, firmado en 1960 con mediación del Banco Mundial, repartió los ríos de la cuenca entre India y Pakistán: los tres orientales para India, los tres occidentales para Pakistán. Durante décadas se lo citó como el ejemplo de que la cooperación por agua es posible incluso entre enemigos, porque sobrevivió a tres guerras entre los dos países.",
            "Su futuro hoy está en discusión, lo que vuelve el ejemplo más útil y no menos: un acuerdo sobre agua puede durar sesenta años y aun así depender de que la relación política que lo rodea no se rompa del todo.",
          ],
        },
        {
          heading: "Lo que pasa cuando nadie negocia",
          paragraphs: [
            "El Mar de Aral es el contraejemplo, y esta app ya lo cuenta en la ruta de los jeans: en la época soviética se desviaron los dos ríos que lo alimentaban para regar algodón en Asia Central, y el que era el cuarto lago más grande del mundo se redujo a una fracción de su tamaño. No hubo conflicto entre países porque en ese momento todos esos territorios estaban dentro de un mismo Estado. El daño lo hizo una decisión administrativa, no una guerra.",
            "El Tigris y el Éufrates, con Turquía aguas arriba de Siria e Irak, y el Mekong, con China aguas arriba de cinco países del sudeste asiático, son las dos cuencas donde esa misma tensión sigue abierta hoy — y donde el cambio climático, al volver los caudales más irregulares, hace que lo que se reparte sea cada vez menos previsible.",
          ],
        },
      ],
    },
    en: {
      title: "Shared rivers: who controls the water",
      summary:
        "Borders divide the land, but rivers cross them. Whoever is upstream holds a lever that whoever is downstream cannot match.",
      sections: [
        {
          heading: "The asymmetry of being upstream",
          paragraphs: [
            "More than half of the fresh water flowing through the world crosses at least one border, and well over a hundred countries share a basin. That creates a power relationship with no equivalent among other resources: oil can be bought from another supplier, but a river comes from where it comes from.",
            "The upstream country can dam, divert or pollute, and the downstream one has no equivalent move in reply. Which is why almost every water conflict ends up negotiated: not because countries are generous, but because the one downstream has nothing to do except negotiate.",
          ],
        },
        {
          heading: "The Nile",
          paragraphs: [
            "Egypt is the extreme case of dependence: almost all the water it uses originates outside its borders, and the country is essentially a river surrounded by desert. For decades that dependence was managed by colonial-era treaties, signed in 1929 and 1959, which split the flow between Egypt and Sudan and did not include Ethiopia — which is where most of the water comes from.",
            "Ethiopia never recognised those agreements, and from 2011 built Africa's largest dam on the Blue Nile, which began filling in 2020. For Ethiopia it is electricity for a country where millions have none. For Egypt it is someone else with a hand on the tap. Negotiations have run for more than a decade without a definitive agreement.",
          ],
        },
        {
          heading: "The Indus, and what it took to work",
          paragraphs: [
            "The Indus Waters Treaty, signed in 1960 with World Bank mediation, divided the basin's rivers between India and Pakistan: the three eastern ones to India, the three western ones to Pakistan. For decades it was cited as proof that cooperation over water is possible even between enemies, because it survived three wars between the two countries.",
            "Its future is now in question, which makes the example more useful rather than less: a water agreement can last sixty years and still depend on the political relationship around it not breaking down entirely.",
          ],
        },
        {
          heading: "What happens when nobody negotiates",
          paragraphs: [
            "The Aral Sea is the counter-example, and this app already tells it on the jeans route: in the Soviet era the two rivers feeding it were diverted to irrigate cotton in Central Asia, and what had been the world's fourth-largest lake shrank to a fraction of its size. There was no conflict between countries because at the time all those territories sat inside one state. The damage was done by an administrative decision, not a war.",
            "The Tigris and Euphrates, with Turkey upstream of Syria and Iraq, and the Mekong, with China upstream of five South-East Asian countries, are the two basins where that same tension is still open today — and where climate change, by making flows more erratic, means what is being divided is ever less predictable.",
          ],
        },
      ],
    },
  },
  {
    slug: "nombres-de-paises",
    slugEn: "country-names",
    emoji: "🏷️",
    category: "foundations",
    related: ["idiomas-del-mundo", "cuantos-paises-hay", "malvinas-falklands"],
    es: {
      title: "Cómo se llama un país, y quién lo decide",
      summary:
        "Casi ningún país se llama, en su propio idioma, como vos lo llamás. Y cuando un nombre cambia, casi nunca es por estética.",
      sections: [
        {
          heading: "Endónimo y exónimo",
          paragraphs: [
            "El nombre que un pueblo se da a sí mismo se llama endónimo; el que le dan desde afuera, exónimo. Alemania es Deutschland para los alemanes, Germany en inglés, Allemagne en francés y Niemcy en polaco. Los cuatro nombres tienen orígenes distintos: uno viene del pueblo mismo, otro del latín Germania, otro de una tribu llamada alamanes, y el polaco de una raíz eslava que significa, más o menos, \"los que no hablan nuestra lengua\".",
            "Japón es Nihon para los japoneses. Llegó a las lenguas europeas por una cadena larga: una lectura china del nombre, tomada por comerciantes malayos, transmitida a los portugueses. Lo que decimos no es una traducción sino el eco de una ruta comercial del siglo XVI.",
          ],
        },
        {
          heading: "Los países que se cambiaron el nombre",
          paragraphs: [
            "Suazilandia pasó a llamarse Esuatini en 2018, por decisión de su rey, para dejar un nombre de origen colonial y de paso terminar con la confusión permanente con Suiza. Turquía pidió en 2022 que se la llamara Türkiye en todos los idiomas ante Naciones Unidas. Alto Volta se convirtió en Burkina Faso en 1984, y el nombre nuevo significa \"tierra de hombres íntegros\" en dos de las lenguas del país.",
            "No todos los cambios se aceptan igual. Birmania pasó a llamarse Myanmar en 1989, pero el cambio lo dispuso una junta militar sin consulta, y por eso durante décadas varios gobiernos y medios siguieron diciendo Birmania. El nombre que usa alguien para ese país es, todavía hoy, una posición política.",
          ],
        },
        {
          heading: "Cuando el nombre es la disputa",
          paragraphs: [
            "Grecia bloqueó durante veintisiete años el ingreso de su vecino del norte a organismos internacionales por llamarse Macedonia, que es también el nombre de una región griega. Se resolvió recién en 2019 con el acuerdo de Prespa y el nombre Macedonia del Norte. Veintisiete años de política exterior por un sustantivo.",
            "Pasa también con el agua: lo que en Irán es el Golfo Pérsico, varios países árabes lo llaman Golfo Arábigo. Lo que en Japón es el Mar de Japón, en Corea es el Mar del Este. Y esta app ya dedica un tema entero a un archipiélago que tiene dos nombres según quién hable. Un nombre no es una etiqueta: es un reclamo abreviado.",
          ],
        },
        {
          heading: "Lo que hace esta app, y lo que no",
          paragraphs: [
            "El dataset de Atlaxis guarda dos nombres para cada uno de los 206 territorios: uno en castellano y otro en inglés. Según qué idioma tengas elegido, el mismo país se llama Alemania o Germany. Es una decisión práctica y funciona bien, pero conviene decir qué deja afuera: ninguno de los dos es, necesariamente, como se llama ese país a sí mismo.",
            "Ese hueco no es exclusivo de esta app —lo tienen los atlas, los mapas escolares y casi todo lo que listé más arriba—, pero vale nombrarlo. La lista de países que estás mirando está escrita desde algún lado, y ese lado tiene un idioma.",
          ],
        },
      ],
    },
    en: {
      title: "What a country is called, and who decides",
      summary:
        "Almost no country is called, in its own language, what you call it. And when a name changes, it is almost never for aesthetics.",
      sections: [
        {
          heading: "Endonym and exonym",
          paragraphs: [
            "The name a people gives itself is an endonym; the one given from outside, an exonym. Germany is Deutschland to Germans, Allemagne in French, Alemania in Spanish and Niemcy in Polish. All four have different origins: one from the people themselves, one from the Latin Germania, one from a tribe called the Alemanni, and the Polish from a Slavic root meaning, roughly, \"those who don't speak our language\".",
            "Japan is Nihon to the Japanese. It reached European languages through a long chain: a Chinese reading of the name, picked up by Malay traders, passed on to the Portuguese. What we say is not a translation but the echo of a sixteenth-century trade route.",
          ],
        },
        {
          heading: "The countries that renamed themselves",
          paragraphs: [
            "Swaziland became Eswatini in 2018, by decision of its king, to shed a name of colonial origin and incidentally end the permanent confusion with Switzerland. Turkey asked in 2022 to be called Türkiye in every language at the United Nations. Upper Volta became Burkina Faso in 1984, the new name meaning \"land of upright people\" in two of the country's languages.",
            "Not every change is accepted the same way. Burma became Myanmar in 1989, but the change was imposed by a military junta without consultation, which is why for decades several governments and outlets went on saying Burma. Which name someone uses for that country is, still today, a political position.",
          ],
        },
        {
          heading: "When the name is the dispute",
          paragraphs: [
            "Greece blocked its northern neighbour's entry into international bodies for twenty-seven years over its being called Macedonia, which is also the name of a Greek region. It was settled only in 2019, with the Prespa agreement and the name North Macedonia. Twenty-seven years of foreign policy over a noun.",
            "It happens with water too: what Iran calls the Persian Gulf, several Arab countries call the Arabian Gulf. What Japan calls the Sea of Japan, Korea calls the East Sea. And this app already devotes a whole topic to an archipelago with two names depending on who is speaking. A name is not a label: it is a claim in shorthand.",
          ],
        },
        {
          heading: "What this app does, and what it doesn't",
          paragraphs: [
            "The Atlaxis dataset holds two names for each of its 206 territories: one in Spanish and one in English. Depending on the language you have chosen, the same country is called Alemania or Germany. It is a practical decision and it works well, but it is worth saying what it leaves out: neither one is necessarily what that country calls itself.",
            "That gap is not unique to this app — atlases, school maps and almost everything listed above have it too — but it is worth naming. The list of countries you are looking at was written from somewhere, and that somewhere has a language.",
          ],
        },
      ],
    },
  },
  {
    slug: "calendario-gregoriano",
    slugEn: "gregorian-calendar",
    emoji: "📆",
    category: "foundations",
    related: ["estado-nacion-westfalia", "religiones-del-mundo", "onu"],
    es: {
      title: "Por qué casi todo el mundo cuenta el mismo año",
      summary:
        "Un calendario nacido de una corrección astronómica del siglo XVI terminó siendo la infraestructura del comercio global — pero no todos los países lo adoptaron, ni al mismo tiempo, ni del todo.",
      sections: [
        {
          heading: "Un error de once minutos por año",
          paragraphs: [
            "El calendario juliano, vigente en Europa desde el año 46 a.C., calculaba el año en 365 días y cuarto. La cifra real es unos once minutos menor. Once minutos no se notan en una vida, pero en mil seiscientos años se acumulan diez días, y hacia el siglo XVI el equinoccio de primavera había corrido tanto que la Pascua —que se fija según ese equinoccio— amenazaba con caer en verano.",
            "En 1582 el papa Gregorio XIII ordenó la corrección: se eliminaron diez días del calendario, de modo que al jueves 4 de octubre le siguió el viernes 15, y se cambió la regla de los años bisiestos para que el desfase no volviera a acumularse. Por eso 1900 no fue bisiesto y 2000 sí lo fue.",
          ],
        },
        {
          heading: "Cuatro siglos para ponerse de acuerdo",
          paragraphs: [
            "Como la reforma venía de Roma, los países católicos la aplicaron enseguida y los demás tardaron, a veces muchísimo. Gran Bretaña y sus colonias recién cambiaron en 1752, y para entonces había que saltear once días: hubo protestas por los alquileres y los salarios de un mes que había durado menos.",
            "Rusia lo adoptó en 1918, ya bajo el gobierno bolchevique. De ahí una rareza que confunde a cualquiera que lea la historia del siglo XX: la Revolución de Octubre ocurrió, para el resto del mundo, en noviembre. Grecia cambió en 1923 y Turquía en 1926, como parte de las reformas de Atatürk. Arabia Saudita usó el calendario islámico para su administración pública hasta 2016.",
            "Ningún tratado obligó a nada de esto. El calendario se volvió universal por la misma vía que se volvieron universales los husos horarios y, más tarde, el UTC: no se puede firmar un contrato, despachar un barco ni fechar un acuerdo con alguien que cuenta los días de otra manera. Es infraestructura, no doctrina.",
          ],
        },
        {
          heading: "Los países que hoy cuentan otro año",
          paragraphs: [
            "Que el calendario gregoriano sea el estándar internacional no significa que sea el único en uso. Etiopía tiene un calendario propio de trece meses —doce de treinta días y uno de cinco o seis— y va entre siete y ocho años por detrás: cuando el resto del mundo entra en 2026, en Etiopía todavía es 2018.",
            "Nepal usa oficialmente el Bikram Sambat, unos cincuenta y siete años por delante. Tailandia fecha sus documentos en era budista, unos 543 años por delante. Taiwán cuenta desde 1912, el año de la fundación de la República de China. Irán usa el calendario hégira solar, y Japón mantiene las eras imperiales, que se reinician con cada emperador.",
            "Y para millones de personas conviven dos: el calendario islámico, lunar y por lo tanto once días más corto que el solar, fija el Ramadán y la peregrinación a La Meca en países que para todo lo demás usan el gregoriano; el calendario hebreo fija las festividades judías; el chino, el Año Nuevo Lunar que se celebra desde Singapur hasta San Francisco. La vida civil corre en un calendario y la religiosa en otro, sin que eso le resulte extraño a nadie.",
          ],
        },
        {
          heading: "Qué queda del acuerdo",
          paragraphs: [
            "Incluso el estándar tiene grietas. La numeración de los años se cuenta desde una fecha cristiana, y por eso el uso académico internacional prefiere hoy \"era común\" (EC / a.EC) a \"después de Cristo\": los números son los mismos, la referencia se vuelve neutral. Las iglesias ortodoxas que nunca adoptaron la reforma siguen celebrando la Navidad el 7 de enero, que es el 25 de diciembre juliano.",
            "La pregunta que deja este tema es la misma que dejan el sistema métrico, los husos horarios o el código de países de dos letras que esta app usa para cada bandera: los estándares globales casi nunca se acordaron entre iguales. Se impusieron desde donde estaba el poder y el resto se acomodó, porque el costo de no hacerlo era quedar afuera del intercambio.",
          ],
        },
      ],
    },
    en: {
      title: "Why almost everyone counts the same year",
      summary:
        "A calendar born from a sixteenth-century astronomical correction became the infrastructure of global trade — but not every country adopted it, nor at the same time, nor completely.",
      sections: [
        {
          heading: "An eleven-minute error a year",
          paragraphs: [
            "The Julian calendar, in force in Europe since 46 BC, put the year at 365 and a quarter days. The real figure is about eleven minutes shorter. Eleven minutes go unnoticed in a lifetime, but over sixteen hundred years they add up to ten days, and by the sixteenth century the spring equinox had drifted so far that Easter — which is fixed by that equinox — was heading for summer.",
            "In 1582 Pope Gregory XIII ordered the correction: ten days were struck from the calendar, so that Thursday 4 October was followed by Friday the 15th, and the leap-year rule was changed so the drift would not build up again. That is why 1900 was not a leap year and 2000 was.",
          ],
        },
        {
          heading: "Four centuries to agree",
          paragraphs: [
            "Because the reform came from Rome, Catholic countries applied it at once and everyone else took their time, sometimes a great deal of it. Britain and its colonies only changed in 1752, and by then eleven days had to be skipped: there were protests over rents and wages for a month that had been shorter than it should.",
            "Russia adopted it in 1918, already under Bolshevik government. Hence an oddity that trips up anyone reading twentieth-century history: the October Revolution happened, for the rest of the world, in November. Greece changed in 1923 and Turkey in 1926, as part of Atatürk's reforms. Saudi Arabia ran its public administration on the Islamic calendar until 2016.",
            "No treaty required any of this. The calendar became universal by the same route that time zones and later UTC did: you cannot sign a contract, clear a ship or date an agreement with someone who counts the days differently. It is infrastructure, not doctrine.",
          ],
        },
        {
          heading: "The countries on a different year today",
          paragraphs: [
            "That the Gregorian calendar is the international standard doesn't make it the only one in use. Ethiopia has a calendar of its own with thirteen months — twelve of thirty days and one of five or six — and runs seven to eight years behind: as the rest of the world enters 2026, Ethiopia is still in 2018.",
            "Nepal officially uses the Bikram Sambat, some fifty-seven years ahead. Thailand dates its documents in the Buddhist era, about 543 years ahead. Taiwan counts from 1912, the founding year of the Republic of China. Iran uses the Solar Hijri calendar, and Japan keeps its imperial eras, which restart with each emperor.",
            "And for millions of people two run side by side: the Islamic calendar, lunar and therefore eleven days shorter than the solar year, fixes Ramadan and the pilgrimage to Mecca in countries that use the Gregorian one for everything else; the Hebrew calendar fixes the Jewish holidays; the Chinese one sets the Lunar New Year celebrated from Singapore to San Francisco. Civil life runs on one calendar and religious life on another, and nobody finds that strange.",
          ],
        },
        {
          heading: "What is left of the agreement",
          paragraphs: [
            "Even the standard has its seams. The years are numbered from a Christian date, which is why international academic usage now prefers \"Common Era\" (CE / BCE) to \"Anno Domini\": the numbers stay the same, the reference becomes neutral. The Orthodox churches that never took up the reform still celebrate Christmas on 7 January, which is 25 December by the Julian calendar.",
            "The question this topic leaves is the one left by the metric system, by time zones, or by the two-letter country codes this app uses for every flag: global standards were almost never agreed between equals. They were set from wherever the power was, and everyone else adjusted, because the cost of not adjusting was being left out of the exchange.",
          ],
        },
      ],
    },
  },
  {
    slug: "estado-nacion-westfalia",
    slugEn: "nation-state-and-westphalia",
    emoji: "🏛️",
    category: "foundations",
    related: [
      "cuantos-paises-hay",
      "onu",
      "deporte-identidad-nacional",
      "democracia-autocracia-sistemas-gobierno",
    ],
    es: {
      title: "El Estado-nación y el sistema westfaliano",
      summary:
        "Por qué el mundo se organiza en países soberanos con fronteras fijas — una idea con apenas 400 años.",
      sections: [
        {
          heading: "¿Qué es un Estado-nación?",
          paragraphs: [
            "Un Estado-nación combina dos ideas: un Estado (una estructura de gobierno con control efectivo sobre un territorio) y una nación (un grupo de personas que comparte una identidad, lengua o historia). No siempre coinciden a la perfección —muchos Estados contienen varios pueblos o naciones, y algunas naciones no tienen Estado propio, como los kurdos— pero esta combinación es la unidad política básica en la que se organiza casi todo el mundo actual, y la que este sitio recorre país por país.",
          ],
        },
        {
          heading: "La Paz de Westfalia (1648)",
          paragraphs: [
            "Antes del siglo XVII, el poder en Europa se repartía entre emperadores, reyes y la Iglesia, con fronteras difusas y superpuestas. Los Tratados de Westfalia, que pusieron fin a la Guerra de los Treinta Años, establecieron un principio nuevo: cada Estado tiene soberanía plena sobre su territorio, sin injerencia externa en sus asuntos internos. Esa idea —conocida como \"soberanía westfaliana\"— es la base del sistema internacional que seguimos usando hoy.",
          ],
        },
        {
          heading: "De Europa al mundo",
          paragraphs: [
            "Durante los siglos siguientes, el modelo de Estado-nación europeo se expandió por el resto del planeta, muchas veces de forma forzada a través de la colonización, y luego se consolidó globalmente con las olas de independencia y descolonización de los siglos XIX y XX. Hoy prácticamente todo el territorio habitado del planeta pertenece a algún Estado soberano reconocido, con las excepciones que se detallan en el artículo sobre cuántos países hay en el mundo.",
          ],
        },
        {
          heading: "Los límites del sistema",
          paragraphs: [
            "El principio de no injerencia convive, en la práctica, con excepciones y tensiones: alianzas militares, organismos como la ONU y el derecho internacional humanitario matizan la soberanía absoluta. Además, no toda \"nación\" tiene su Estado propio: pueblos como los kurdos o los saharauis reclaman una soberanía que todavía no consiguieron.",
          ],
        },
      ],
    },
    en: {
      title: "The nation-state and the Westphalian system",
      summary:
        "Why the world is organized into sovereign countries with fixed borders — an idea barely 400 years old.",
      sections: [
        {
          heading: "What is a nation-state?",
          paragraphs: [
            "A nation-state combines two ideas: a state (a government structure with effective control over a territory) and a nation (a group of people who share an identity, language or history). The two don't always line up perfectly — many states contain several peoples or nations, and some nations have no state of their own, like the Kurds — but this combination is the basic political unit that organizes almost the entire modern world, the same one this site walks through country by country.",
          ],
        },
        {
          heading: "The Peace of Westphalia (1648)",
          paragraphs: [
            "Before the 17th century, power in Europe was split between emperors, kings and the Church, with blurry, overlapping borders. The Peace of Westphalia, which ended the Thirty Years' War, established a new principle: each state holds full sovereignty over its territory, free from outside interference in its internal affairs. That idea — known as \"Westphalian sovereignty\" — underpins the international system still in use today.",
          ],
        },
        {
          heading: "From Europe to the world",
          paragraphs: [
            "Over the following centuries, the European nation-state model spread across the rest of the planet, often by force through colonization, and later became globally consolidated through the waves of independence and decolonization of the 19th and 20th centuries. Today, nearly all inhabited territory on the planet belongs to some recognized sovereign state, with the exceptions detailed in the article on how many countries there are in the world.",
          ],
        },
        {
          heading: "The limits of the system",
          paragraphs: [
            "The principle of non-interference coexists, in practice, with exceptions and tensions: military alliances, bodies like the UN, and international humanitarian law all qualify absolute sovereignty. And not every \"nation\" has its own state: peoples like the Kurds or the Sahrawis claim a sovereignty they still haven't achieved.",
          ],
        },
      ],
    },
  },
  {
    slug: "onu",
    slugEn: "united-nations",
    emoji: "🇺🇳",
    category: "foundations",
    related: ["guerras-mundiales", "otan", "estado-nacion-westfalia"],
    es: {
      title: "La ONU: qué es y cómo funciona",
      summary:
        "El organismo que casi todos los países del mundo integran — y por qué a veces no puede actuar.",
      sections: [
        {
          heading: "Origen y propósito",
          paragraphs: [
            "Creada en 1945, al final de la Segunda Guerra Mundial, con el objetivo declarado de evitar un tercer conflicto global, fomentar la cooperación internacional y proteger los derechos humanos. Reemplazó a la fallida Sociedad de Naciones surgida tras la Primera Guerra Mundial. Hoy tiene 193 estados miembros.",
          ],
        },
        {
          heading: "Cómo está organizada",
          paragraphs: [
            "La Asamblea General reúne a los 193 miembros, cada uno con un voto, pero sus resoluciones no son de cumplimiento obligatorio. El Consejo de Seguridad, en cambio, sí puede tomar decisiones vinculantes: lo integran 15 países, 5 de ellos permanentes (Estados Unidos, Rusia, China, Reino Unido y Francia) con derecho a veto sobre cualquier resolución. Otros organismos incluyen la Corte Internacional de Justicia y agencias especializadas como la OMS o UNICEF.",
          ],
        },
        {
          heading: "El veto: la clave y el límite del sistema",
          paragraphs: [
            "Que cualquiera de los 5 miembros permanentes pueda bloquear una resolución con un solo voto explica tanto la estabilidad del sistema —evita que el Consejo actúe sin el acuerdo de las grandes potencias— como su parálisis más criticada: los conflictos donde alguno de los 5 tiene intereses directos, o los de un aliado cercano, rara vez logran una resolución vinculante.",
          ],
        },
        {
          heading: "Logros y críticas",
          paragraphs: [
            "Entre sus logros se cuentan miles de misiones de paz, la Declaración Universal de los Derechos Humanos (1948) y la coordinación de la respuesta a crisis humanitarias globales. Entre las críticas más habituales: la falta de representación de África y América Latina entre los miembros permanentes, y la dificultad para actuar cuando los intereses de las grandes potencias están en juego.",
          ],
        },
      ],
    },
    en: {
      title: "The UN: what it is and how it works",
      summary:
        "The body that almost every country in the world belongs to — and why it sometimes can't act.",
      sections: [
        {
          heading: "Origin and purpose",
          paragraphs: [
            "Created in 1945, at the end of World War II, with the stated goal of preventing a third global conflict, fostering international cooperation and protecting human rights. It replaced the failed League of Nations that emerged after World War I. It now has 193 member states.",
          ],
        },
        {
          heading: "How it's organized",
          paragraphs: [
            "The General Assembly brings together all 193 members, each with one vote, but its resolutions aren't binding. The Security Council, by contrast, can make binding decisions: it's made up of 15 countries, 5 of them permanent (the United States, Russia, China, the United Kingdom and France) with veto power over any resolution. Other bodies include the International Court of Justice and specialized agencies like the WHO or UNICEF.",
          ],
        },
        {
          heading: "The veto: the system's key feature and its limit",
          paragraphs: [
            "The fact that any of the 5 permanent members can block a resolution with a single vote explains both the system's stability — it keeps the Council from acting without the great powers' agreement — and its most criticized flaw: conflicts where one of the 5, or a close ally of theirs, has a direct stake rarely produce a binding resolution.",
          ],
        },
        {
          heading: "Achievements and criticisms",
          paragraphs: [
            "Its achievements include thousands of peacekeeping missions, the Universal Declaration of Human Rights (1948), and coordinating the response to global humanitarian crises. Among the most common criticisms: the lack of representation for Africa and Latin America among the permanent members, and the difficulty of acting when the great powers' own interests are at stake.",
          ],
        },
      ],
    },
  },
  {
    slug: "union-europea",
    slugEn: "european-union",
    emoji: "🇪🇺",
    category: "geopolitics",
    related: [
      "commonwealth",
      "otan",
      "union-africana-panafricanismo",
    ],
    es: {
      title: "La Unión Europea: de la posguerra a la integración",
      summary:
        "Cómo seis países que buscaban evitar otra guerra terminaron creando el bloque económico y político más integrado del mundo.",
      sections: [
        {
          heading: "El origen: carbón y acero contra la guerra",
          paragraphs: [
            "En 1951, apenas seis años después del fin de la Segunda Guerra Mundial, Francia, Alemania Occidental, Italia, Bélgica, Países Bajos y Luxemburgo crearon la Comunidad Europea del Carbón y del Acero. La idea era simple pero radical: si estos dos recursos clave para hacer la guerra quedaban bajo control compartido, un nuevo conflicto entre Francia y Alemania se volvería mucho más difícil.",
          ],
        },
        {
          heading: "De comunidad económica a unión política",
          paragraphs: [
            "Ese proyecto fue ampliándose durante décadas hasta el Tratado de Maastricht (1993), que creó formalmente la Unión Europea con instituciones propias —Parlamento Europeo, Comisión Europea— y el objetivo de una integración cada vez mayor. Hoy tiene 27 países miembros.",
          ],
        },
        {
          heading: "El euro y el espacio Schengen",
          paragraphs: [
            "En 1999 se lanzó el euro, la moneda compartida hoy por 20 de los 27 miembros (y usada también, sin ser miembros de la Unión, por Mónaco, San Marino, Andorra y la Ciudad del Vaticano). El espacio Schengen, por su parte, eliminó los controles fronterizos entre la mayoría de los países miembros, permitiendo circular sin pasaporte de un país a otro.",
          ],
        },
        {
          heading: "Una integración con límites",
          paragraphs: [
            "No todos los países miembros adoptaron el euro (Suecia y Polonia, entre otros, mantienen su propia moneda), y en 2020 el Reino Unido se convirtió en el primer país en abandonar la Unión —el \"Brexit\"—, mostrando que esta integración, aunque profunda, sigue siendo una decisión voluntaria de cada Estado miembro.",
          ],
        },
      ],
    },
    en: {
      title: "The European Union: from postwar recovery to integration",
      summary:
        "How six countries trying to prevent another war ended up building the world's most integrated economic and political bloc.",
      sections: [
        {
          heading: "The origin: coal and steel against war",
          paragraphs: [
            "In 1951, just six years after the end of World War II, France, West Germany, Italy, Belgium, the Netherlands and Luxembourg created the European Coal and Steel Community. The idea was simple but radical: if these two resources critical to waging war were placed under shared control, a new conflict between France and Germany would become far harder to fight.",
          ],
        },
        {
          heading: "From economic community to political union",
          paragraphs: [
            "That project kept expanding over the following decades until the Maastricht Treaty (1993), which formally created the European Union with its own institutions — the European Parliament, the European Commission — and the goal of ever-closer integration. It now has 27 member countries.",
          ],
        },
        {
          heading: "The euro and the Schengen Area",
          paragraphs: [
            "The euro launched in 1999 and is now shared by 20 of the 27 members (and also used, without being EU members, by Monaco, San Marino, Andorra and Vatican City). The Schengen Area, for its part, removed border checks between most member countries, allowing passport-free travel from one to another.",
          ],
        },
        {
          heading: "An integration with limits",
          paragraphs: [
            "Not every member country adopted the euro (Sweden and Poland, among others, kept their own currency), and in 2020 the United Kingdom became the first country to leave the Union — \"Brexit\" — showing that this integration, however deep, remains a voluntary choice each member state can walk back.",
          ],
        },
      ],
    },
  },
  {
    slug: "revolucion-industrial",
    slugEn: "industrial-revolution",
    emoji: "⚙️",
    category: "development",
    related: ["capitalismo-y-alternativas", "colonizacion-divisiones", "esclavitud-comercio-triangular"],
    es: {
      title: "La Revolución Industrial",
      summary:
        "El cambio económico y social que, en apenas un siglo, transformó la forma en que el mundo produce, trabaja y vive.",
      sections: [
        {
          heading: "De los talleres a las fábricas",
          paragraphs: [
            "A partir de aproximadamente 1760, Gran Bretaña comenzó a reemplazar la producción manual y artesanal por máquinas movidas primero por agua y luego por la máquina de vapor, perfeccionada por James Watt. Sectores como el textil y la siderurgia fueron los primeros en transformarse, multiplicando su capacidad de producción.",
          ],
        },
        {
          heading: "Urbanización y nueva clase obrera",
          paragraphs: [
            "La concentración de fábricas en las ciudades provocó una migración masiva del campo a los centros urbanos, dando origen a una nueva clase social —el proletariado industrial— que trabajaba largas jornadas en condiciones muchas veces muy duras, lo que impulsó, con el tiempo, los primeros movimientos y sindicatos obreros.",
          ],
        },
        {
          heading: "De Gran Bretaña al resto del mundo",
          paragraphs: [
            "El proceso se expandió durante el siglo XIX a Europa continental, Estados Unidos y Japón, ampliando la brecha económica entre estos países industrializados y el resto del planeta —una brecha que en muchos casos persiste hasta hoy— y también intensificó el reparto colonial de África y Asia, ya que las potencias industriales necesitaban materias primas y nuevos mercados.",
          ],
        },
        {
          heading: "Su legado en el mundo actual",
          paragraphs: [
            "La Revolución Industrial sentó las bases del capitalismo moderno, del sistema de fábricas que todavía organiza buena parte de la producción global, y del propio concepto de \"crecimiento económico\" como objetivo de política de Estado: la misma idea que hoy se mide con el PBI.",
          ],
        },
      ],
    },
    en: {
      title: "The Industrial Revolution",
      summary:
        "The economic and social shift that, in barely a century, transformed how the world produces, works and lives.",
      sections: [
        {
          heading: "From workshops to factories",
          paragraphs: [
            "Starting around 1760, Britain began replacing manual, craft-based production with machines powered first by water and then by the steam engine, perfected by James Watt. Sectors like textiles and iron and steel were the first to be transformed, multiplying their output.",
          ],
        },
        {
          heading: "Urbanization and a new working class",
          paragraphs: [
            "The concentration of factories in cities drove a massive migration from the countryside into urban centers, giving rise to a new social class — the industrial working class — that worked long hours in often very harsh conditions, which over time fueled the first labor movements and unions.",
          ],
        },
        {
          heading: "From Britain to the rest of the world",
          paragraphs: [
            "The process spread through the 19th century to continental Europe, the United States and Japan, widening the economic gap between these industrialized countries and the rest of the planet — a gap that in many cases persists today — and also intensified the colonial carving-up of Africa and Asia, as industrial powers needed raw materials and new markets.",
          ],
        },
        {
          heading: "Its legacy in today's world",
          paragraphs: [
            "The Industrial Revolution laid the groundwork for modern capitalism, for the factory system that still organizes much of global production, and for the very concept of \"economic growth\" as a goal of state policy — the same idea measured today through GDP.",
          ],
        },
      ],
    },
  },
  {
    slug: "esclavitud-comercio-triangular",
    slugEn: "slavery-and-triangular-trade",
    emoji: "⛓️",
    category: "history",
    related: [
      "colonizacion-divisiones",
      "revolucion-industrial",
      "migracion-diasporas",
      "arte-literatura-musica",
    ],
    es: {
      title: "La esclavitud y el comercio triangular",
      summary:
        "Cómo un sistema de comercio forzado entre tres continentes moldeó la demografía y la economía del mundo atlántico durante más de tres siglos.",
      sections: [
        {
          heading: "Cómo funcionaba el comercio triangular",
          paragraphs: [
            "Entre los siglos XVI y XIX, barcos europeos zarpaban hacia África occidental cargados de productos manufacturados, que intercambiaban por personas esclavizadas. Esas personas eran trasladadas por la fuerza a través del Atlántico —el trayecto conocido como el \"Paso del Medio\"— hacia América, donde eran vendidas para trabajar en plantaciones de azúcar, algodón y tabaco. Desde América, barcos cargados con esas materias primas volvían a Europa, cerrando el circuito.",
          ],
        },
        {
          heading: "Una escala sin precedentes",
          paragraphs: [
            "Se estima que entre 12 y 13 millones de personas africanas fueron trasladadas por la fuerza hacia América durante estos siglos, en condiciones extremas durante la travesía que causaron la muerte de aproximadamente el 15% de quienes eran embarcados. Fue uno de los mayores desplazamientos forzados de la historia humana.",
          ],
        },
        {
          heading: "Su huella en el mundo actual",
          paragraphs: [
            "Este sistema explica en gran parte la composición demográfica actual de países como Brasil, Estados Unidos, Haití y buena parte del Caribe, y dejó marcas económicas y sociales profundas: tanto en las desigualdades raciales que persisten en las Américas como en el desarrollo económico desigual entre Europa occidental y África occidental.",
          ],
        },
        {
          heading: "El camino hacia la abolición",
          paragraphs: [
            "El Reino Unido prohibió el comercio de personas esclavizadas en 1807 y la esclavitud misma en la mayoría de sus colonias en 1833; Estados Unidos la abolió en 1865, al final de su Guerra Civil; Brasil fue el último país de América en hacerlo, en 1888. La abolición no significó el fin inmediato de la discriminación racial, cuyas consecuencias siguieron —y siguen— siendo parte central de la vida política de muchos de estos países.",
          ],
        },
      ],
    },
    en: {
      title: "Slavery and the triangular trade",
      summary:
        "How a system of forced trade across three continents shaped the demographics and economy of the Atlantic world for over three centuries.",
      sections: [
        {
          heading: "How the triangular trade worked",
          paragraphs: [
            "Between the 16th and 19th centuries, European ships sailed to West Africa loaded with manufactured goods, which they traded for enslaved people. Those people were forcibly transported across the Atlantic — the crossing known as the \"Middle Passage\" — to the Americas, where they were sold to work on sugar, cotton and tobacco plantations. From the Americas, ships loaded with those raw materials sailed back to Europe, closing the loop.",
          ],
        },
        {
          heading: "A scale without precedent",
          paragraphs: [
            "An estimated 12 to 13 million African people were forcibly transported to the Americas over these centuries, under extreme conditions during the crossing that killed roughly 15% of those who were boarded. It was one of the largest forced displacements in human history.",
          ],
        },
        {
          heading: "Its mark on today's world",
          paragraphs: [
            "This system largely explains the present-day demographic makeup of countries like Brazil, the United States, Haiti and much of the Caribbean, and it left deep economic and social scars — both in the racial inequalities that persist across the Americas and in the uneven economic development between Western Europe and West Africa.",
          ],
        },
        {
          heading: "The road to abolition",
          paragraphs: [
            "The United Kingdom banned the trade in enslaved people in 1807 and slavery itself across most of its colonies in 1833; the United States abolished it in 1865, at the end of its Civil War; Brazil was the last country in the Americas to do so, in 1888. Abolition didn't mean an immediate end to racial discrimination, whose consequences went on being — and still are — a central part of political life in many of these countries.",
          ],
        },
      ],
    },
  },
  {
    slug: "cambio-climatico",
    slugEn: "climate-change",
    emoji: "🌡️",
    category: "development",
    related: ["pbi-idh", "primer-tercer-mundo", "capitalismo-y-alternativas"],
    es: {
      title: "Cambio climático y cooperación global",
      summary:
        "Por qué ningún país puede resolver este problema solo, y cómo el mundo intenta —con dificultad— coordinarse para hacerlo.",
      sections: [
        {
          heading: "El problema, en pocas palabras",
          paragraphs: [
            "Desde la Revolución Industrial, la quema de combustibles fósiles (carbón, petróleo, gas) liberó a la atmósfera enormes cantidades de gases de efecto invernadero, elevando la temperatura promedio del planeta. Ese calentamiento intensifica fenómenos como sequías, olas de calor, inundaciones y la suba del nivel del mar, con efectos que no respetan fronteras nacionales.",
          ],
        },
        {
          heading: "Por qué es un problema distinto a otros",
          paragraphs: [
            "A diferencia de un conflicto entre dos países, el cambio climático es lo que se conoce como un problema de \"acción colectiva\": las emisiones de cualquier país afectan al resto del planeta, pero cada país tiene un incentivo individual para no limitar su propia actividad económica si los demás tampoco lo hacen. Por eso requiere una cooperación internacional de una escala poco habitual.",
          ],
        },
        {
          heading: "El debate sobre la responsabilidad histórica",
          paragraphs: [
            "Los países hoy desarrollados son responsables de la gran mayoría de las emisiones acumuladas desde la Revolución Industrial, mientras que muchos países en desarrollo —que contribuyeron mucho menos al problema— sufren hoy algunos de sus efectos más severos. Ese desequilibrio está en el centro de la mayoría de las negociaciones climáticas internacionales.",
          ],
        },
        {
          heading: "Los acuerdos internacionales",
          paragraphs: [
            "El Protocolo de Kioto (1997) fue el primer tratado en fijar límites de emisiones para países desarrollados. El Acuerdo de París (2015), firmado por prácticamente todos los países del mundo, estableció el objetivo de limitar el aumento de la temperatura global muy por debajo de los 2°C, aunque, a diferencia de Kioto, sus metas por país no son legalmente vinculantes: otra muestra de la tensión entre la soberanía nacional y la cooperación global.",
          ],
        },
      ],
    },
    en: {
      title: "Climate change and global cooperation",
      summary:
        "Why no single country can solve this problem alone, and how the world tries — with difficulty — to coordinate on it.",
      sections: [
        {
          heading: "The problem, in a nutshell",
          paragraphs: [
            "Since the Industrial Revolution, burning fossil fuels (coal, oil, gas) has released enormous amounts of greenhouse gases into the atmosphere, raising the planet's average temperature. That warming intensifies phenomena like droughts, heat waves, floods and rising sea levels, with effects that don't respect national borders.",
          ],
        },
        {
          heading: "Why it's a different kind of problem",
          paragraphs: [
            "Unlike a conflict between two countries, climate change is what's known as a \"collective action\" problem: any country's emissions affect the rest of the planet, but each country has an individual incentive not to limit its own economic activity if others don't do the same. That's why it requires international cooperation on an unusually large scale.",
          ],
        },
        {
          heading: "The debate over historical responsibility",
          paragraphs: [
            "Today's developed countries are responsible for the vast majority of emissions accumulated since the Industrial Revolution, while many developing countries — which contributed far less to the problem — suffer some of its most severe effects today. That imbalance sits at the center of most international climate negotiations.",
          ],
        },
        {
          heading: "The international agreements",
          paragraphs: [
            "The Kyoto Protocol (1997) was the first treaty to set emissions limits for developed countries. The Paris Agreement (2015), signed by virtually every country in the world, set the goal of keeping the global temperature rise well below 2°C, though, unlike Kyoto, its country-by-country targets aren't legally binding — another instance of the tension between national sovereignty and global cooperation.",
          ],
        },
      ],
    },
  },
  {
    slug: "democracia-autocracia-sistemas-gobierno",
    slugEn: "democracy-and-autocracy",
    emoji: "🗳️",
    category: "foundations",
    related: [
      "monarquias",
      "estado-nacion-westfalia",
      "primer-tercer-mundo",
    ],
    es: {
      title: "Democracia, autocracia y sistemas de gobierno",
      summary:
        "Las distintas formas en que los países deciden quién manda y cómo: la clasificación política más básica para entender cualquier país.",
      sections: [
        {
          heading: "Democracias: presidencialismo vs. parlamentarismo",
          paragraphs: [
            "En una democracia presidencialista (Estados Unidos, Argentina, Brasil), la ciudadanía elige por separado a un presidente —jefe de Estado y de gobierno a la vez— y a un parlamento, con mandatos fijos e independientes entre sí. En una democracia parlamentaria (Reino Unido, España, Alemania, India), la ciudadanía elige un parlamento, y este a su vez designa a un primer ministro como jefe de gobierno, que puede ser reemplazado en cualquier momento si pierde el respaldo parlamentario.",
            "Muchas monarquías constitucionales (ver el artículo relacionado) funcionan, en la práctica, como democracias parlamentarias con un jefe de Estado hereditario en lugar de uno electo.",
          ],
        },
        {
          heading: "Autocracias: cuando el poder no rota",
          paragraphs: [
            "Una autocracia concentra el poder en una persona o un grupo reducido, sin elecciones competitivas reales que permitan removerlos. Puede tomar la forma de un partido único (China), una dictadura militar —con ejemplos históricos en buena parte de América Latina durante el siglo XX—, una monarquía absoluta (ver el artículo sobre monarquías) o una teocracia, donde el poder político está subordinado a una autoridad religiosa, como en Irán desde 1979.",
          ],
        },
        {
          heading: "Los sistemas híbridos",
          paragraphs: [
            "Entre ambos extremos existe una amplia zona gris: países que celebran elecciones pero con una competencia limitada, medios de comunicación controlados o una oposición debilitada. Los politólogos suelen llamarlos \"regímenes híbridos\" o \"democracias iliberales\", y representan hoy una porción considerable de los países del mundo según los principales índices internacionales de democracia.",
          ],
        },
        {
          heading: "Por qué esta clasificación no es fija",
          paragraphs: [
            "Los sistemas de gobierno cambian: golpes de Estado, revoluciones, transiciones democráticas o el ascenso de líderes autoritarios dentro de democracias existentes pueden modificar la clasificación de un país en pocos años. Medir esto de forma objetiva y comparable entre países es, en sí mismo, un desafío permanente para los organismos internacionales y académicos que elaboran estos índices.",
          ],
        },
      ],
    },
    en: {
      title: "Democracy, autocracy and systems of government",
      summary:
        "The different ways countries decide who's in charge and how — the most basic political classification for understanding any country.",
      sections: [
        {
          heading: "Democracies: presidential vs. parliamentary",
          paragraphs: [
            "In a presidential democracy (the United States, Argentina, Brazil), citizens separately elect a president — head of state and head of government at once — and a parliament, each with fixed, independent terms. In a parliamentary democracy (the United Kingdom, Spain, Germany, India), citizens elect a parliament, which in turn appoints a prime minister as head of government, who can be replaced at any time if they lose parliamentary support.",
            "Many constitutional monarchies (see the related article) function, in practice, as parliamentary democracies with a hereditary rather than elected head of state.",
          ],
        },
        {
          heading: "Autocracies: when power doesn't rotate",
          paragraphs: [
            "An autocracy concentrates power in one person or a small group, without real competitive elections that could remove them. It can take the form of a single-party state (China), a military dictatorship — with historical examples across much of Latin America during the 20th century —, an absolute monarchy (see the article on monarchies), or a theocracy, where political power is subordinated to a religious authority, as in Iran since 1979.",
          ],
        },
        {
          heading: "Hybrid systems",
          paragraphs: [
            "Between both extremes lies a wide gray zone: countries that hold elections but with limited competition, controlled media, or a weakened opposition. Political scientists often call these \"hybrid regimes\" or \"illiberal democracies,\" and they represent a considerable share of the world's countries today according to major international democracy indices.",
          ],
        },
        {
          heading: "Why this classification isn't fixed",
          paragraphs: [
            "Systems of government change: coups, revolutions, democratic transitions, or the rise of authoritarian leaders within existing democracies can shift a country's classification within just a few years. Measuring this objectively and comparably across countries is, in itself, an ongoing challenge for the international bodies and academics who build these indices.",
          ],
        },
      ],
    },
  },
  {
    slug: "revolucion-rusa",
    slugEn: "russian-revolution",
    emoji: "🚩",
    category: "history",
    related: ["guerra-fria", "guerras-mundiales", "capitalismo-y-alternativas"],
    es: {
      title: "La Revolución Rusa y el nacimiento de la URSS",
      summary:
        "Cómo un imperio gobernado por un zar se convirtió, en apenas un año, en el primer Estado comunista de la historia.",
      sections: [
        {
          heading: "Un imperio al borde del colapso",
          paragraphs: [
            "Para 1917, el Imperio Ruso, gobernado por el zar Nicolás II, acumulaba décadas de tensiones: una economía agraria atrasada, una participación desastrosa en la Primera Guerra Mundial —con millones de bajas— y una desigualdad social profunda entre una aristocracia terrateniente y una masa campesina y obrera empobrecida.",
          ],
        },
        {
          heading: "Dos revoluciones en un mismo año",
          paragraphs: [
            "En febrero de 1917 (según el calendario ruso de la época), protestas y motines forzaron la abdicación del zar, dando paso a un gobierno provisional. Pero ese gobierno no logró salir de la guerra ni resolver la crisis social, y en octubre de ese mismo año los bolcheviques, liderados por Vladímir Lenin, tomaron el poder en un segundo golpe revolucionario, prometiendo \"paz, tierra y pan\".",
          ],
        },
        {
          heading: "La guerra civil y la consolidación bolchevique",
          paragraphs: [
            "Entre 1918 y 1922, el Ejército Rojo bolchevique combatió contra una coalición de fuerzas opositoras conocida como el Ejército Blanco, en una sangrienta guerra civil. Su victoria permitió consolidar el poder soviético y, en diciembre de 1922, formalizar la creación de la Unión de Repúblicas Socialistas Soviéticas (URSS), el primer Estado del mundo gobernado bajo una ideología comunista.",
          ],
        },
        {
          heading: "Su onda expansiva en el siglo XX",
          paragraphs: [
            "La Revolución Rusa demostró que un movimiento comunista podía tomar y conservar el poder de un Estado, inspirando movimientos similares en China, Cuba y buena parte del mundo durante el siglo XX, y estableciendo a la URSS como la superpotencia que protagonizaría, junto a Estados Unidos, toda la Guerra Fría (ver el artículo relacionado).",
          ],
        },
      ],
    },
    en: {
      title: "The Russian Revolution and the birth of the USSR",
      summary:
        "How an empire ruled by a tsar became, in barely a year, the first communist state in history.",
      sections: [
        {
          heading: "An empire on the brink of collapse",
          paragraphs: [
            "By 1917, the Russian Empire, ruled by Tsar Nicholas II, had accumulated decades of tension: a backward agrarian economy, a disastrous involvement in the First World War — with millions of casualties — and deep social inequality between a landowning aristocracy and an impoverished peasant and worker majority.",
          ],
        },
        {
          heading: "Two revolutions in a single year",
          paragraphs: [
            "In February 1917 (by the Russian calendar of the time), protests and mutinies forced the tsar's abdication, giving way to a provisional government. But that government failed to exit the war or resolve the social crisis, and in October of that same year the Bolsheviks, led by Vladimir Lenin, seized power in a second revolutionary coup, promising \"peace, land, and bread.\"",
          ],
        },
        {
          heading: "The civil war and Bolshevik consolidation",
          paragraphs: [
            "Between 1918 and 1922, the Bolshevik Red Army fought a coalition of opposing forces known as the White Army in a bloody civil war. Its victory allowed the Soviets to consolidate power and, in December 1922, formalize the creation of the Union of Soviet Socialist Republics (USSR), the world's first state governed under a communist ideology.",
          ],
        },
        {
          heading: "Its ripple effect across the 20th century",
          paragraphs: [
            "The Russian Revolution showed that a communist movement could seize and hold state power, inspiring similar movements in China, Cuba, and much of the world throughout the 20th century, and establishing the USSR as the superpower that would drive, alongside the United States, the entire Cold War (see the related article).",
          ],
        },
      ],
    },
  },
  {
    slug: "independencia-india-particion",
    slugEn: "indian-independence-and-partition",
    emoji: "✂️",
    category: "history",
    related: [
      "colonizacion-divisiones",
      "religiones-del-mundo",
      "migracion-diasporas",
    ],
    es: {
      title: "La independencia de la India y la Partición (1947)",
      summary:
        "El fin del dominio británico en el subcontinente indio terminó en la creación de dos países y uno de los desplazamientos masivos más grandes de la historia.",
      sections: [
        {
          heading: "El fin del Raj británico",
          paragraphs: [
            "El Reino Unido gobernó el subcontinente indio durante casi dos siglos, primero a través de la Compañía Británica de las Indias Orientales y luego, desde 1858, de forma directa —el llamado \"Raj británico\"—. El movimiento independentista, liderado por figuras como Mahatma Gandhi y su estrategia de resistencia no violenta, ganó fuerza durante la primera mitad del siglo XX, especialmente después de la Segunda Guerra Mundial, que dejó al Reino Unido debilitado y sin recursos para sostener el control colonial.",
          ],
        },
        {
          heading: "La decisión de dividir el territorio",
          paragraphs: [
            "Ante las crecientes tensiones entre la mayoría hindú y la minoría musulmana del subcontinente, el plan británico de independencia de 1947 incluyó una partición: se crearon dos países separados, la India (de mayoría hindú) y Pakistán (de mayoría musulmana, dividido a su vez en dos alas, oriental y occidental, hasta la independencia de Bangladés en 1971).",
          ],
        },
        {
          heading: "Uno de los mayores desplazamientos de la historia",
          paragraphs: [
            "La partición provocó la migración forzada de entre 10 y 20 millones de personas —hindúes y sijs hacia la India, musulmanes hacia Pakistán— en cuestión de meses, junto con episodios de violencia sectaria que causaron cientos de miles de muertos. Es considerado uno de los mayores desplazamientos forzados de población en la historia humana (ver el artículo sobre migraciones y diásporas).",
          ],
        },
        {
          heading: "Un conflicto que sigue abierto",
          paragraphs: [
            "India y Pakistán se convirtieron en rivales desde su fundación, con tres guerras libradas desde 1947 y un conflicto territorial sin resolver por la región de Cachemira, reclamada por ambos países y, en parte, también por China. Ambos países son hoy potencias nucleares, lo que convierte a esta frontera en una de las más tensas del mundo.",
          ],
        },
      ],
    },
    en: {
      title: "Indian independence and Partition (1947)",
      summary:
        "The end of British rule over the Indian subcontinent led to the creation of two countries and one of the largest mass displacements in history.",
      sections: [
        {
          heading: "The end of the British Raj",
          paragraphs: [
            "The United Kingdom ruled the Indian subcontinent for nearly two centuries, first through the British East India Company and then, from 1858 onward, directly — the so-called \"British Raj.\" The independence movement, led by figures such as Mahatma Gandhi and his strategy of nonviolent resistance, gained strength through the first half of the 20th century, especially after the Second World War left the United Kingdom weakened and without the resources to sustain colonial control.",
          ],
        },
        {
          heading: "The decision to divide the territory",
          paragraphs: [
            "Facing rising tensions between the subcontinent's Hindu majority and Muslim minority, the British independence plan of 1947 included a partition: two separate countries were created, India (Hindu-majority) and Pakistan (Muslim-majority, itself split into an eastern and a western wing until Bangladesh's independence in 1971).",
          ],
        },
        {
          heading: "One of history's largest displacements",
          paragraphs: [
            "Partition triggered the forced migration of an estimated 10 to 20 million people — Hindus and Sikhs toward India, Muslims toward Pakistan — within a matter of months, alongside episodes of sectarian violence that killed hundreds of thousands. It is considered one of the largest forced population displacements in human history (see the article on migration and diasporas).",
          ],
        },
        {
          heading: "A conflict that remains open",
          paragraphs: [
            "India and Pakistan became rivals from the moment of their founding, fighting three wars since 1947 and remaining locked in an unresolved territorial dispute over the Kashmir region, claimed by both countries and, in part, by China as well. Both countries are nuclear powers today, making this border one of the tensest in the world.",
          ],
        },
      ],
    },
  },
  {
    slug: "union-africana-panafricanismo",
    slugEn: "african-union-and-panafricanism",
    emoji: "🦁",
    category: "geopolitics",
    related: ["colonizacion-divisiones", "union-europea", "commonwealth"],
    es: {
      title: "La Unión Africana y el panafricanismo",
      summary:
        "Cómo el continente con más países del mundo construyó su propio proyecto de integración política, a contramano de sus fronteras heredadas del colonialismo.",
      sections: [
        {
          heading: "El panafricanismo como idea",
          paragraphs: [
            "Desde principios del siglo XX, intelectuales y líderes africanos y de la diáspora africana —como W. E. B. Du Bois o Kwame Nkrumah— impulsaron el panafricanismo: la idea de que los pueblos africanos, dispersos por el colonialismo y la esclavitud (ver el artículo sobre esclavitud y comercio triangular), comparten una identidad y un destino común que debía traducirse en una mayor unidad política y económica del continente.",
          ],
        },
        {
          heading: "De la OUA a la Unión Africana",
          paragraphs: [
            "En 1963, apenas tres años después del \"Año de África\" (ver el artículo sobre colonización y divisiones geopolíticas), 32 países recién independizados crearon la Organización para la Unidad Africana (OUA), centrada sobre todo en apoyar los procesos de descolonización pendientes y combatir el apartheid en Sudáfrica. En 2002 la OUA fue reemplazada por la Unión Africana (UA), con un mandato más amplio de integración económica y política, inspirado explícitamente en el modelo de la Unión Europea.",
          ],
        },
        {
          heading: "Un continente, muchas fronteras heredadas",
          paragraphs: [
            "A diferencia de la Unión Europea, la Unión Africana integra a 55 Estados miembros —casi el 28% de los países del mundo— con fronteras que, en su gran mayoría, fueron trazadas por potencias coloniales sin considerar las divisiones étnicas o culturales preexistentes. Esa combinación de diversidad extrema y fronteras heredadas hace que la integración africana enfrente desafíos distintos a los de la experiencia europea.",
          ],
        },
        {
          heading: "Logros y limitaciones",
          paragraphs: [
            "La Unión Africana impulsa desde 2018 el Área de Libre Comercio Continental Africana, uno de los bloques de libre comercio más grandes del mundo por cantidad de países miembros, y ha mediado en conflictos internos de varios de sus miembros. Sin embargo, al igual que la ONU, su capacidad de acción suele depender del consenso entre sus Estados miembros, lo que limita su poder frente a crisis internas de gran escala.",
          ],
        },
      ],
    },
    en: {
      title: "The African Union and pan-Africanism",
      summary:
        "How the continent with the most countries in the world built its own political integration project, working against borders inherited from colonialism.",
      sections: [
        {
          heading: "Pan-Africanism as an idea",
          paragraphs: [
            "Since the early 20th century, intellectuals and leaders from Africa and the African diaspora — such as W. E. B. Du Bois or Kwame Nkrumah — championed pan-Africanism: the idea that African peoples, scattered by colonialism and slavery (see the article on slavery and the triangular trade), share a common identity and destiny that should translate into greater political and economic unity across the continent.",
          ],
        },
        {
          heading: "From the OAU to the African Union",
          paragraphs: [
            "In 1963, just three years after the \"Year of Africa\" (see the article on colonization and geopolitical divisions), 32 newly independent countries founded the Organisation of African Unity (OAU), focused mainly on supporting ongoing decolonization and fighting apartheid in South Africa. In 2002 the OAU was replaced by the African Union (AU), with a broader mandate for economic and political integration, explicitly inspired by the European Union model.",
          ],
        },
        {
          heading: "One continent, many inherited borders",
          paragraphs: [
            "Unlike the European Union, the African Union brings together 55 member states — nearly 28% of the world's countries — with borders that were, for the most part, drawn by colonial powers without regard for pre-existing ethnic or cultural divisions. That combination of extreme diversity and inherited borders means African integration faces challenges quite different from the European experience.",
          ],
        },
        {
          heading: "Achievements and limitations",
          paragraphs: [
            "Since 2018 the African Union has driven the African Continental Free Trade Area, one of the largest free-trade blocs in the world by number of member countries, and it has mediated internal conflicts among several of its members. However, like the UN, its capacity to act usually depends on consensus among its member states, which limits its power in the face of large-scale internal crises.",
          ],
        },
      ],
    },
  },
  {
    slug: "arte-literatura-musica",
    slugEn: "art-literature-and-music",
    emoji: "🎨",
    category: "society",
    related: [
      "deporte-identidad-nacional",
      "esclavitud-comercio-triangular",
      "idiomas-del-mundo",
    ],
    es: {
      title: "Arte, literatura y música: expresiones culturales del mundo",
      summary:
        "Cómo el arte, los libros y la música se convirtieron en una de las formas más duraderas de identidad nacional y regional.",
      sections: [
        {
          heading: "La literatura y el reconocimiento global",
          paragraphs: [
            "El Premio Nobel de Literatura, entregado desde 1901, es uno de los principales termómetros del reconocimiento literario internacional, aunque su historia también refleja desequilibrios: durante buena parte del siglo XX, la gran mayoría de los premiados fueron autores europeos, y recién en las últimas décadas se diversificó de forma más marcada hacia autores de África, Asia y América Latina, como parte de un reconocimiento más amplio de tradiciones literarias históricamente menos visibilizadas fuera de sus regiones.",
          ],
        },
        {
          heading: "Movimientos artísticos y su huella geográfica",
          paragraphs: [
            "Muchos movimientos del arte visual están profundamente asociados a un lugar y un momento histórico: el Renacimiento en la Italia del siglo XV, el muralismo mexicano posrevolucionario —con figuras como Diego Rivera o David Alfaro Siqueiros— como herramienta de identidad nacional, o las tradiciones textiles y escultóricas de distintos pueblos de África occidental, muchas veces desplazadas a museos europeos durante la era colonial: un tema hoy en el centro de debates internacionales sobre restitución de patrimonio.",
          ],
        },
        {
          heading: "La música como identidad nacional y de exportación",
          paragraphs: [
            "Géneros musicales enteros nacieron ligados a la identidad de un país o una región: el tango en Argentina y Uruguay, el jazz y el blues en Estados Unidos —con raíces directas en la experiencia afroamericana, ver el artículo sobre esclavitud y comercio triangular—, el reggae en Jamaica o el afrobeat en Nigeria. En las últimas décadas, géneros como el K-pop surcoreano muestran cómo la música puede convertirse también en una herramienta deliberada de \"poder blando\" (ver el artículo sobre deporte e identidad nacional) para proyectar la imagen de un país en el resto del mundo.",
          ],
        },
        {
          heading: "Cultura como patrimonio compartido y disputado",
          paragraphs: [
            "Cuando una expresión artística cruza fronteras —ya sea por migración, comercio o colonización— a menudo surgen debates sobre a quién \"pertenece\" esa tradición cultural, y quién tiene derecho a beneficiarse económicamente de ella. Estos debates son cada vez más frecuentes a medida que la cultura circula globalmente a través de internet y las plataformas de streaming.",
          ],
        },
      ],
    },
    en: {
      title: "Art, literature and music: the world's cultural expressions",
      summary:
        "How art, books and music became one of the most enduring forms of national and regional identity.",
      sections: [
        {
          heading: "Literature and global recognition",
          paragraphs: [
            "The Nobel Prize in Literature, awarded since 1901, is one of the main barometers of international literary recognition, though its history also reflects imbalances: for much of the 20th century, the vast majority of laureates were European authors, and only in recent decades has the prize diversified more markedly toward authors from Africa, Asia, and Latin America, as part of a broader recognition of literary traditions historically less visible outside their own regions.",
          ],
        },
        {
          heading: "Artistic movements and their geographic footprint",
          paragraphs: [
            "Many visual-art movements are deeply tied to a place and a historical moment: the Renaissance in 15th-century Italy, Mexico's post-revolutionary muralism — with figures such as Diego Rivera or David Alfaro Siqueiros — as a tool of national identity, or the textile and sculptural traditions of various West African peoples, often displaced to European museums during the colonial era: an issue now at the center of international debates over the restitution of cultural heritage.",
          ],
        },
        {
          heading: "Music as national identity and export",
          paragraphs: [
            "Entire musical genres were born tied to the identity of a country or a region: tango in Argentina and Uruguay, jazz and blues in the United States — with roots directly tracing back to the African American experience, see the article on slavery and the triangular trade —, reggae in Jamaica, or Afrobeat in Nigeria. In recent decades, genres such as South Korean K-pop show how music can also become a deliberate tool of \"soft power\" (see the article on sport and national identity) to project a country's image to the rest of the world.",
          ],
        },
        {
          heading: "Culture as shared and contested heritage",
          paragraphs: [
            "When an artistic expression crosses borders — whether through migration, trade, or colonization — debates often arise over who a cultural tradition \"belongs\" to, and who has the right to profit economically from it. These debates are becoming more frequent as culture circulates globally through the internet and streaming platforms.",
          ],
        },
      ],
    },
  },
  {
    slug: "patrimonio-humanidad-turismo",
    slugEn: "world-heritage-and-tourism",
    emoji: "🏺",
    category: "society",
    related: [
      "religiones-del-mundo",
      "colonizacion-divisiones",
      "arte-literatura-musica",
    ],
    es: {
      title: "El Patrimonio de la Humanidad y el turismo cultural",
      summary:
        "Los más de 1.200 sitios que la UNESCO considera parte de la herencia común de la humanidad, y por qué esa lista también genera disputas.",
      sections: [
        {
          heading: "Qué es un sitio Patrimonio de la Humanidad",
          paragraphs: [
            "Desde 1972, la UNESCO —la agencia de la ONU para la educación, la ciencia y la cultura— mantiene una lista de lugares, naturales, culturales o mixtos, considerados de un valor tan excepcional que se los declara patrimonio de toda la humanidad y no solo del país donde se encuentran. Hoy la lista supera los 1.200 sitios en más de 160 países.",
          ],
        },
        {
          heading: "Quién tiene más (y quién tiene menos)",
          paragraphs: [
            "Italia y China encabezan el ranking de países con más sitios declarados, mientras que varios países pequeños o con menores recursos económicos para afrontar las costosas postulaciones tienen pocos o ningún sitio en la lista, lo que generó críticas sobre un sesgo hacia el patrimonio de países ricos, especialmente europeos, en las primeras décadas de la lista.",
          ],
        },
        {
          heading: "Patrimonio en peligro y patrimonio disputado",
          paragraphs: [
            "La UNESCO mantiene además una lista de \"Patrimonio Mundial en Peligro\", para sitios amenazados por conflictos armados, el cambio climático o el desarrollo urbano descontrolado. Algunos sitios, además, están en el centro de disputas territoriales o históricas —como ocurre con templos y ciudades reclamados por más de un país o comunidad—, lo que convierte a la declaración de patrimonio, en esos casos, en un gesto también político.",
          ],
        },
        {
          heading: "El turismo como motor y como amenaza",
          paragraphs: [
            "La declaración de un sitio como Patrimonio de la Humanidad suele disparar el turismo internacional, con un impacto económico enorme para las comunidades locales, pero también genera el fenómeno conocido como \"sobreturismo\": una afluencia de visitantes tan grande que termina deteriorando el propio sitio que se buscaba proteger, un desafío creciente en destinos como Venecia o Machu Picchu.",
          ],
        },
      ],
    },
    en: {
      title: "World Heritage and cultural tourism",
      summary:
        "The 1,200-plus sites UNESCO considers part of humanity's shared heritage — and why that list also generates disputes.",
      sections: [
        {
          heading: "What a World Heritage site is",
          paragraphs: [
            "Since 1972, UNESCO — the UN agency for education, science, and culture — has maintained a list of places, whether natural, cultural, or mixed, considered so exceptional in value that they're declared heritage of all humanity, not just of the country where they're located. The list today numbers over 1,200 sites across more than 160 countries.",
          ],
        },
        {
          heading: "Who has more (and who has less)",
          paragraphs: [
            "Italy and China top the ranking of countries with the most listed sites, while several smaller countries, or those with fewer economic resources to cover the costly nomination process, have few or no sites on the list — sparking criticism over a bias toward wealthy, especially European, heritage during the list's early decades.",
          ],
        },
        {
          heading: "Heritage in danger and heritage in dispute",
          paragraphs: [
            "UNESCO also maintains a \"World Heritage in Danger\" list for sites threatened by armed conflict, climate change, or uncontrolled urban development. Some sites are also at the center of territorial or historical disputes — as happens with temples and cities claimed by more than one country or community — which turns a heritage designation, in those cases, into a political gesture as well.",
          ],
        },
        {
          heading: "Tourism as an engine and a threat",
          paragraphs: [
            "Being declared a World Heritage site tends to spark a surge in international tourism, bringing a huge economic impact for local communities, but it also fuels the phenomenon known as \"overtourism\": an influx of visitors so large that it ends up degrading the very site it was meant to protect — a growing challenge in destinations such as Venice or Machu Picchu.",
          ],
        },
      ],
    },
  },
];

// Slugs that were live before a topic was renamed or split. The URLs are
// public and may be bookmarked or linked, so they resolve to the article
// that inherited the subject rather than to "topic not found".
const RETIRED_SLUGS: Record<string, string> = {
  // Split into two: NATO kept the security half, the IMF got its own.
  "otan-fmi": "otan",
};

export const findLearnTopic = (slug?: string) => {
  const resolved = (slug && RETIRED_SLUGS[slug]) || slug;
  return learnTopics.find(
    (topic) => topic.slug === resolved || topic.slugEn === resolved
  );
};
